// Objednávka / checkout — všechny kroky nativního 3krokového wizardu Shoptetu.
// Redesign 2026-08-21 dle handoveru `design_handoff_objednavka/README.md`
// (prototyp objednavka-v2a-split-sidebar.dc.html; předchozí zadání
// screens/06-objednavka.md tímto NEPLATÍ — vč. dřívějšího „slim" režimu kroků
// 2–3 bez trust pásu, nový design má trust pás a patičku na všech krocích).
// Delta 2026-09-02 dle `design_handoff_objednavka_v3/README.md` (prototyp
// „Objednavka v3 - sync dev.dc.html" je od té doby závazný): sleva položky ve
// sloupci součtu, mobilní rozložení položky a termínů, jeden progress bar
// výhod se dvěma milníky (v souhrnu nic), škála hmotnosti místo vět o pásmu,
// mobilní skupiny dopravců bez medailonku a s odsazenými podvolbami.
//
// Nativní wizard: `/kosik/` (krok 1) → `/objednavka/krok-1/` (doprava a platba)
// → `/objednavka/krok-2/` (informace o vás). Pole, pořadí ani validace NEMĚNÍME,
// stavíme vizuální vrstvu:
//   • topbar = logo + stepper (nativní `ol.cart-header` PŘESUNUTÝ dovnitř) +
//     telefon; sdílí split mřížku s obsahem, takže sloupce lícují
//   • split layout 55/45: obsah vlevo (max 760), šedý sticky souhrn vpravo
//     (max 420); šedý tint jede až k pravému okraji viewportu (gradient)
//   • krok 1 = `table.cart-table` překlopená na řádky; termín doručení,
//     progress výhod (dárek/doprava zdarma z dataLayer `cartInfo`) a odkaz
//     zpět jsou součást karty košíku; souhrn vpravo staví položky z tabulky
//   • krok 2 = `.radio-wrapper` seskupené do řádkových skupin (výdejní místa /
//     na adresu / osobní odběr) v jedné kartě; platby = akordeon s info pásem
//     z nativního tooltipu; volbu metody pouští `mousedown` dispatch (viz níže)
//   • krok 3 = nativní fieldsety jako karty se šedým hlavičkovým pásem,
//     `#company-shopping` jako switch v pásu Fakturační adresy
//   • rekapitulace = nativní `#summary-box` + fotky položek přenesené
//     z košíku přes `sessionStorage` (nativní souhrn obrázky nemá)
//   • mobil: fixní spodní lišta (Celkem k úhradě + CTA), rekapitulace zůstává
//     viditelná pod obsahem (handover: záměrně se neskrývá do akordeonu)
//
// Co se do implementace ZÁMĚRNĚ nedostalo (není v datech / platformě):
//   • karta „Volitelné doplňkové služby" — Shoptet žádné doplňkové služby
//     v košíku nenese, řádky by neměly co objednat (admin/data operace)
//   • tag „Nejlevnější" (zrušen 2026-08-31, viz níž); „Nejrychlejší" se počítá
//     z reálných cen (jen když je minimum jednoznačné)
//   • termín per podvolba dopravy — Shoptet umí jen jeden termín (fáze B
//     per dopravce viz CLAUDE.md); termín nese hlavička skupiny
//   • jméno dárku v progress baru — `cartInfo` nese jen částky, ne název
//
// Parita s prototypem se hlídá číselně: `node tools/audit/checkout-parity.mjs`
// (měří tytéž prvky v prototypu i na dev instanci; 2026-08-21 dotaženo z 213
// rozdílů na desktopu).
//
// Idempotentní (`html.bs-co-ready`, `data-bs-co`); košík se po každém AJAX
// přepočtu dekoruje znovu (watchCartUpdates), souhrn kroků 2–3 hlídá vlastní
// observer (watchSummaryUpdates).

import { icon } from './icon.js';
import { money, withCurrency } from './money.js';
import L from '../locale/cs/checkoutPage.js';
import { buildWeightScale } from './weightScale.js';
import { carrierLogo, carrierLogoId, carrierTag } from './logo.js';
import { ensureLogos } from './injectSprite.js';
import { CONTACT, phonesOn, reachContact } from './headerTools.js';
import { asChatTrigger } from './supportChat.js';
import { shoptetData, onCartUpdated, cartLeft } from '../shoptet-hooks.js';
import { freeShippingState } from './benefitBand.js';
import { cartSkeletonActive, endCartSkeleton, startCartSkeleton } from './cartSkeleton.js';
import { HAS_REVIEWS } from './reviews.js';
import { heurekaPercent } from './reviewsModel.js';
import { createSaleEnd, saleEndsFor } from './saleEnd.js';
import { discountPercent } from './productPrice.js';
import { ensurePrices, knownPrices, wholesaleAdvantage, wholesaleCustomer } from './wholesalePrice.js';
import { VO } from '../locale/cs/common.js';
import SHOP from '../../config/shop.js';
import { renderCartCrossSell } from './cartCrossSell.js';
import { activeCampaign } from './campaign.js';
import {
  deliveryTimeline,
  fastCarrier,
  fastOffered,
  fastFirst,
  fastPromo,
  nativeDeliveryText,
  LABEL_FASTEST,
} from './deliveryTimeline.js';
import {
  SHIP_GROUPS,
  loadShippingOptions,
  OVERSIZE_RE,
  OVERSIZE_NOTE,
  PICKUP_ADDRESS,
  PICKUP_MAP_URL,
  PICKUP_MAP_LABEL,
  PICKUP_STOCK_NOTE,
  PICKUP_HOURS_LABEL,
  FREE_SHIP_MAX_KG,
  FREE_SHIP_FROM_FALLBACK,
  FREE_SHIPPING,
  HAS_WEIGHT_BANDS,
  WEIGHT_BAND_PRICES,
  WEIGHT_CHIP_LEAD,
  WEIGHT_SCALE_NOTE_A,
  WEIGHT_FOOT_OVER,
  pickupStatus,
  nextPickupDay,
  openingHours,
  shippingGroupKey,
  freeShipExcluded as cartFreeShipExcluded,
  cartWeight,
  bandOf,
  formatKg,
  paymentIcon,
  shippingPromo,
  isPromoRow,
} from './shippingModel.js';
import { bodyClass, page } from './pages.js';

// escape, ne literál — soubor v repu měl místo U+00A0 obyčejnou mezeru (nález
// 2026-09-02: „15–30 kg" i „199 Kč" se na mobilu lámaly uprostřed)
const NBSP = '\u00A0';

// --- copy ------------------------------------------------------------------
const CONTINUE_SHOPPING = L.continueShopping;
const RELATED_MORE = L.relatedMore;
// hlavička karty doplňkových služeb fvStudio (prototyp v2a)
const SERVICES = {
  title: L.servicesTitle,
  note: L.servicesNote,
};
const GIFT_LABEL = L.giftLabel;
const SUM_TITLE = L.sumTitle;
const HINT_NEXT = L.hintNext;
const GOODS_LABEL = L.goodsLabel;
const SAVED_LABEL = L.savedLabel; // žlutý pásek pod „Cena bez DPH" (v3 §7.3)
// tmavá varianta pásku pro velkoobchod (wholesalePrice.js, schváleno 2026-09-13)
const SAVED_VO_LABEL = VO.savedWithPricelist;
const SAVED_VO_GENERIC = VO.savedGeneric;
const TOTAL_LABEL = L.totalLabel;
const NOTE_PLACEHOLDER = L.notePlaceholder;
const PICK_NOTE = L.pickNote;
const COUPON_APPLY = L.couponApply;
const COUPON_PLACEHOLDER = L.couponPlaceholder;
const CTA_LABELS = { cart: L.ctaCart, shipping: L.ctaShipping };
const BACK_LABELS = { shipping: L.backShipping, info: L.backInfo };
const PAY_SECURE = L.paySecure;
const SHIP_TITLE = L.shipTitle;
const PAY_TITLE = L.payTitle;
const CONTACT_TITLE = L.contactTitle;
const CART_PICKUP_LEAD = L.cartPickupLead;
// zvláštní závoz dopravce v košíku (deliveryTimeline.js `t.fast`: GLS sobota
// čt 15:00 → pá 14:30, PPL neděle pá 14:30 → so 24:00): dřívější termín nahradí
// nativní v řádku doručení, dovětek nese `t.fast.tag` („s GLS" / „s PPL")

// Skupiny dopravy (výdejní místa / adresa / osobní odběr), regex nadrozměrné
// přepravy i vysvětlující text OVERSIZE_NOTE drží `shippingModel.js` — sdílí
// je modal „Možnosti doručení" na detailu produktu, aby zákazník viděl na obou
// místech stejné rozdělení i vysvětlení (#225).

// Trust pás — práh dopravy zdarma je datová věc shopu (dataLayer), ne mockupu.
const TRUST = [
  { icon: 'shield', title: L.trustSecureTitle, text: L.trustSecureText },
  { icon: 'truck', title: L.trustFreeShipTitle, text: null, key: 'freeShipping' },
  { icon: 'refresh', title: L.trustReturnTitle, text: L.trustReturnText },
  { icon: 'headset', title: L.trustHelpTitle, text: null, key: 'phone' },
];

// Košík vyloučený z dopravy zdarma (nadrozměr, a v shopu bez dopravy zdarma
// každý košík) dlaždici o dopravě zdarma nedostane — slibovala by výdejní
// místa, která v takové objednávce nejsou v nabídce vůbec (rozhodnutí Tomáše
// 2026-08-25). Náhrada musí platit VŽDY, proto hodnocení obchodu (totéž číslo
// nese patička i panel na homepage); shop bez hodnoticí platformy (SK) místo
// něj ukáže cenu dopravy z ceníku, ne cizí hodnocení.
// SK hodnocení téže firmy ukazuje jen v patičce (manifest `reviews.checkoutTile`).
// Číslo = řádek Heureky z manifestu v její nativní metrice (recenze U2, schválil
// Tomáš 6. 10.: „Na Heurece nás doporučuje 96 % zákazníků"; dřív tu stálo napevno
// „4,8 z 5"). Heureka v `/reviews/summary` není (ze s1 403), patička bere totéž.
const HEUREKA_PCT = heurekaPercent(SHOP.reviews.platforms);
const TRUST_NO_FREE_SHIP = HAS_REVIEWS && SHOP.reviews.checkoutTile && HEUREKA_PCT !== null
  ? { icon: 'star', title: L.trustShopTitle, text: L.trustShopText(HEUREKA_PCT) }
  : { icon: 'truck', title: L.trustShipFromTitle, text: L.trustShipFromText(money(WEIGHT_BAND_PRICES[0].box)) };

// Název shopu — copyright, alt loga a textový fallback, když obrázek loga chybí
const SHOP_NAME = SHOP.shop.name;

const FOOT_LINKS = [
  { href: page('terms'), label: L.footTerms },
  { href: page('shipping'), label: L.footShipping },
  { href: page('privacy'), label: L.footPrivacy },
  { href: page('contact'), label: L.footContact },
];

const EMPTY = {
  title: L.emptyTitle,
  text: L.emptyText,
  cta: L.emptyCta,
};

// Kontakt = sdílený `CONTACT` z headerTools.js (manifest shopu). Help v hlavičce
// pokladny slibuje poradenství → `CONTACT.hours` = pracovní doba PODPORY, ne
// otevírací doba prodejny.

// --- helpery ---------------------------------------------------------------
function node(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

function link(href, className, text) {
  const a = node('a', className, text);
  a.href = href;
  return a;
}

function setText(el, text) {
  // zápis jen při změně — dekorace běží i z MutationObserverů a bezpodmínečný
  // zápis by je krmil vlastní mutací
  if (el && el.textContent !== text) el.textContent = text;
}

// „1 položka / 2 položky / 5 položek"
function itemsWord(n) {
  return L.items(n);
}

const priceText = (value) => money(value);

// Doprava zdarma platí v tomhle shopu JEN pro výdejní místa (admin → Doprava
// a platby → tabulka „Doprava zdarma od částky": PPL a GLS na výdejní místo).
// Ověřeno v kroku 2 na koši 2 814 Kč: výdejní místa Zdarma, PPL na adresu
// 99 Kč, GLS na adresu 119 Kč. Dokud tenhle rozsah dataLayer nenese, drží ho
// texty — změní-li se pravidlo v adminu, mění se i tady.
// (hodnotu drží `shippingModel.js`, sdílí ji modal možností doručení)

// Práh dopravy zdarma: dataLayer má přednost; košík nad 10 kg ho nenese
// (Shoptet nemá použitelný řádek zdarma → `freeShippingFrom: null`), tam
// platí zrcadlo ceníku FREE_SHIP_FROM_FALLBACK (shippingModel.js).
function freeShipFromText() {
  const info = shoptetData('cartInfo');
  return priceText(info && info.freeShippingFrom) || priceText(FREE_SHIP_FROM_FALLBACK);
}

// Hmotnostní tarif (2026-09-02, pásma po 10 kg od 2026-09-05): doprava zdarma jen do 10 kg — dlaždice to
// říká rovnou, aby těžký košík nečekal zdarma.
function freeShippingText() {
  return L.freeShipping(freeShipFromText(), FREE_SHIP_MAX_KG);
}

// Pásmo ceníku pro aktuální košík (null bez váhy v datech — pak se nic
// z hmotnostní komunikace nekreslí, nikdy se neodhaduje).
function cartBand() {
  const info = shoptetData('cartInfo');
  const kg = cartWeight(info);
  if (kg == null) return null;
  const band = bandOf(kg);
  return band ? { kg: kg, band: band } : null;
}

// Neprázdný košík BEZ prahu = Shoptet dopravu zdarma pro tohle zboží vyloučil
// (nadrozměr, košík nad 10 kg) — definice sdílená s promo pruhem
// (`shippingModel.js`). Prázdný košík nebo nenačtený dataLayer práh jen zatím
// nezná — tam se dlaždice nechává, jen s obecným textem.
function freeShipExcluded() {
  return cartFreeShipExcluded(shoptetData('cartInfo'));
}

// Číslo z nativní cenovky metody („99 Kč" → 99, „ZDARMA" → 0).
function methodPrice(wrapper) {
  const el = wrapper.querySelector('.payment-shipping-price');
  if (!el) return null;
  if (el.classList.contains('for-free')) return 0;
  const digits = (el.textContent || '').replace(/[\s  ]/g, '').match(/-?\d+([.,]\d+)?/);
  if (!digits) return 0;
  return parseFloat(digits[0].replace(',', '.'));
}

// Shoptet nadpisy sekcí pokladny už jednou tiše přeznačil (h4→h2, 2026-08-09) —
// proto libovolná úroveň.
function heading(box) {
  return box.querySelector('h2, h3, h4');
}

// --- slim chrome: topbar se stepperem --------------------------------------
// Topbar sdílí split mřížku s obsahem (levá buňka logo + stepper, pravá telefon),
// takže sloupce lícují se sloupci obsahu (handover §Layout).
function buildHead() {
  if (document.querySelector('.bs-co-head')) return;
  const header = document.getElementById('header');
  const head = node('header', 'bs-co-head');
  const grid = node('div', 'bs-co-head__grid');
  head.appendChild(grid);

  const left = node('div', 'bs-co-head__left');
  const leftIn = node('div', 'bs-co-head__in');
  left.appendChild(leftIn);
  grid.appendChild(left);

  // logo PŘESOUVÁME z nativní hlavičky (kopie = druhý odkaz na homepage)
  const logo = document.querySelector('#header .site-name');
  if (logo) {
    logo.classList.add('bs-co-head__logo');
    leftIn.appendChild(logo);
  } else {
    leftIn.appendChild(link('/', 'bs-co-head__logo bs-co-head__logo--text', SHOP_NAME));
  }
  // stepper doplní buildStepper() — sem patří jen cíl přesunu; pružné mezery
  // kolem něj jsou z prototypu (logo | spacer | stepper | spacer, gap 26)
  leftIn.appendChild(node('span', 'bs-co-head__spacer'));
  leftIn.appendChild(node('div', 'bs-co-head__steps'));
  leftIn.appendChild(node('span', 'bs-co-head__spacer'));

  const right = node('div', 'bs-co-head__right');
  const rightIn = node('div', 'bs-co-head__in bs-co-head__in--aside');
  right.appendChild(rightIn);
  grid.appendChild(right);

  // chat → e-mail → telefon (reachContact v headerTools): se zapnutým chatem
  // je nápověda pokladny vstup do chatu (plovoucí tlačítko tu není), e-mail
  // zůstává v trust pásu pod obsahem
  const reach = reachContact();
  const help = link(reach.href, 'bs-co-head__help');
  help.setAttribute('aria-label', reach.chat ? reach.text : reach.phone ? L.helpCall(reach.text) : L.helpMail(reach.text));
  help.setAttribute('rel', 'nofollow');
  if (reach.chat) asChatTrigger(help, 'checkout');
  const ic = node('span', 'bs-co-head__help-ic');
  ic.appendChild(icon('headset'));
  help.appendChild(ic);
  const txt = node('span', 'bs-co-head__help-txt');
  txt.appendChild(node('strong', null, reach.text));
  txt.appendChild(node('span', null, L.helpHours(CONTACT.hours)));
  help.appendChild(txt);
  rightIn.appendChild(help);

  if (header && header.parentNode) header.parentNode.insertBefore(head, header);
  else document.body.insertBefore(head, document.body.firstChild);
}

// Trust pás + řádková patička — na VŠECH krocích (handover; na mobilu je skrývá
// CSS, rozhodnutí klienta „na mobilu zbytečné").
// Jedna dlaždice trust pásu. Dlaždice s `key` si klíč nese v datasetu, aby
// šla po AJAX změně košíku vyměnit, aniž se přestavuje celá patička.
function trustItem(t) {
  const spec = t.key === 'freeShipping' && freeShipExcluded() ? TRUST_NO_FREE_SHIP : t;
  const item = node('div', 'bs-co-trust__item');
  if (t.key) item.dataset.bsTrust = t.key;
  const ic = node('span', 'bs-co-trust__ic');
  ic.appendChild(icon(spec.icon));
  item.appendChild(ic);
  const body = node('span', 'bs-co-trust__txt');
  body.appendChild(node('strong', null, spec.title));
  let text = spec.text;
  if (spec.key === 'freeShipping') text = freeShippingText();
  if (spec.key === 'phone') text = phonesOn() ? CONTACT.phone : CONTACT.email;
  body.appendChild(node('span', null, text || ''));
  item.appendChild(body);
  return item;
}

// Po AJAX změně košíku se stav „vyloučeno z dopravy zdarma" mění (odebrání
// nadrozměrné položky) — patička se staví jednou, tak se překreslí ta dlaždice.
function refreshTrust() {
  const old = document.querySelector('.bs-co-trust__item[data-bs-trust="freeShipping"]');
  if (!old) return;
  const spec = TRUST.find(function (t) {
    return t.key === 'freeShipping';
  });
  old.replaceWith(trustItem(spec));
}

// Odkaz na homepage s LOGEM shopu. Nativní obrázek loga si pokladna stěhuje do
// topbaru (`buildHead`), takže tady se KLONUJE — druhý `.site-name` v DOM by
// Shoptetu přebil vlastní úpravy hlavičky. Bez obrázku (blank instance, výpadek
// CDN) zůstává textová varianta, kterou patička nesla do 2026-09-05.
function shopLogoLink(className) {
  const a = link('/', className, null);
  const img = document.querySelector('.bs-co-head__logo img, #header .site-name img');
  if (img && img.getAttribute('src')) {
    const clone = img.cloneNode(false);
    clone.removeAttribute('class');
    clone.removeAttribute('id');
    clone.setAttribute('alt', SHOP_NAME);
    clone.setAttribute('loading', 'lazy');
    a.appendChild(clone);
  } else {
    a.classList.add(className + '--text');
    a.textContent = SHOP_NAME;
  }
  return a;
}

function buildFoot() {
  if (document.querySelector('.bs-co-foot')) return;
  const anchor = document.getElementById('content-wrapper');
  if (!anchor || !anchor.parentNode) return;

  const foot = node('div', 'bs-co-foot');
  const trust = node('div', 'bs-co-trust');
  const trustIn = node('div', 'bs-co-trust__in');
  for (const t of TRUST) {
    trustIn.appendChild(trustItem(t));
  }
  trust.appendChild(trustIn);
  foot.appendChild(trust);

  const mini = node('div', 'bs-co-mini');
  const miniIn = node('div', 'bs-co-mini__in');
  miniIn.appendChild(shopLogoLink('bs-co-mini__logo'));
  const nav = node('nav', 'bs-co-mini__links');
  for (const l of FOOT_LINKS) nav.appendChild(link(l.href, 'bs-co-mini__link', l.label));
  miniIn.appendChild(nav);
  miniIn.appendChild(
    node('span', 'bs-co-mini__copy', '© 2015–' + new Date().getFullYear() + ' ' + SHOP_NAME)
  );
  mini.appendChild(miniIn);
  foot.appendChild(mini);

  anchor.parentNode.insertBefore(foot, anchor.nextSibling);
}

// --- stepper ---------------------------------------------------------------
// Nativní `ol.cart-header` se stěhuje do topbaru; popisky jsou platformní
// („Nákupní košík / Doprava & platba / Informace o vás" = přesně texty designu).
function buildStepper() {
  const list = document.querySelector('ol.cart-header');
  if (!list || list.dataset.bsCo) return;
  list.dataset.bsCo = '1';
  list.classList.add('bs-co-steps');

  const target = document.querySelector('.bs-co-head__steps');
  if (target) target.appendChild(list);

  const steps = list.querySelectorAll('li.step');
  let index = 0;
  for (const li of steps) {
    index += 1;
    li.classList.add('bs-co-step');
    const done = li.classList.contains('completed');
    const badge = node('span', 'bs-co-step__badge');
    if (done) badge.appendChild(icon('check'));
    else badge.appendChild(node('span', 'bs-co-step__num', String(index)));
    const label = li.querySelector('a > span, strong > span');
    if (label) label.classList.add('bs-co-step__label');
    const holder = li.querySelector('a, strong');
    if (holder) holder.insertBefore(badge, holder.firstChild);
    else li.insertBefore(badge, li.firstChild);
  }
}

// --- výhody: jeden progress bar se dvěma milníky (handover v3 §3) -----------
// Zdroj = dataLayer `cartInfo`. Chování platformy ověřené na dev 2026-08-21
// (sondy `ops/sweeps` nejsou potřeba, je to čitelné z `cartInfo`):
//   • po dosažení dopravy zdarma je `leftToFreeShipping.priceLeft` ZÁPORNÝ
//     (−1 283 Kč) a `freeShipping` zůstává `false` → dosažení se pozná z `<= 0`,
//     ne z rovnosti nule (jinak bar hlásí „Ještě  do dopravy zdarma" bez částky),
//   • `freeShippingFrom` je `null`, když košík na dopravu zdarma nemá nárok —
//     nadrozměr (třeba benzínový křovinořez) i, od hmotnostního tarifu
//     (2026-09-02), košík nad 10 kg → milník dopravy se NEKRESLÍ, ať je důvod
//     jakýkoli (rozhodnutí Tomáše 2026-09-03: „bez prahu žádný slib"). Dřívější
//     nedosažitelný milník „2 000 Kč · do 7 kg" u těžkého košíku zákazník četl
//     jako chybu — výplň přejela přes milník a ikona zůstala šedá. Hmotnost
//     vysvětlí až škála v kroku 2, v košíku se o ní nepíše nic (v3 §4.1),
//   • práh dárku dataLayer nenese; dopočítat jde jen dokud NENÍ dosažený
//     (zboží + zbývá), po dosažení se čte ze zapamatované hodnoty. Bez ní se
//     dárek ukáže jen v hlavičce baru, milník bez známé pozice se nekreslí.
// Bar je JEN v kartě košíku — souhrn kroku 1 ani kroků 2–3 výhody nenese
// (prototyp v3: „v sidebaru žádný duplikát").
const MS_HEAD_BOTH = L.msHeadBoth;
const MS_HEAD_GIFT = L.msHeadGift;
// prototyp tenhle stav nemá (práh dárku je nižší než dopravy), promo se ale
// může změnit — dosažená doprava bez dárku nesmí hlásit „Vaše výhody"
const MS_HEAD_SHIP = L.msHeadShip;
const MS_HEAD_NONE = L.msHeadNone;
const MS_LABEL_GIFT = L.msLabelGift;
const MS_LABEL_SHIP = L.msLabelShip;
// dovětek o hmotnostním stropu dopravy zdarma; shop bez pásem (plochý tarif)
// ani bez dopravy zdarma milník nekreslí vůbec
const MS_SHIP_KG = FREE_SHIPPING && HAS_WEIGHT_BANDS ? L.msShipKg(FREE_SHIP_MAX_KG) : '';
const MS_BAR_LABEL = L.msBarLabel;
const MS_HEADROOM = 1.12; // konec škály = 1,12 × největší z prahů a hodnoty zboží (prototyp)
const GIFT_KEY = 'bsCoGiftAt';
const THRESHOLD_TTL = 7 * 24 * 60 * 60 * 1000;

// Prahy promo akcí jsou konstanty shopu, ne košíku → pamatují se i mezi
// návštěvami (localStorage, TTL 7 dní); čerstvá hodnota vždy přepíše starou.
// Zapisuje se i do sessionStorage — localStorage neexistuje ve všech
// kontextech (privátní režim, `file://` v offline matici), degradace je pak
// tichá: zpráva o dárku zůstane, jen bez milníku na škále.
function readThreshold(store, key) {
  try {
    const raw = JSON.parse(store.getItem(key) || 'null');
    if (!raw || !Number.isFinite(raw.v) || raw.v <= 0) return null;
    if (!Number.isFinite(raw.t) || Date.now() - raw.t > THRESHOLD_TTL) return null;
    return raw.v;
  } catch (e) {
    return null;
  }
}

// Samotné ČTENÍ `window.localStorage` hází SecurityError, když má prohlížeč
// úložiště zakázané (Chrome „Blokovat všechny cookies“, sandboxovaný iframe) —
// proto přes getter v try, ne v poli literálu (code review 2026-09-09, B1-01).
function storageOrNull(name) {
  try {
    return window[name] || null;
  } catch (e) {
    return null;
  }
}

function rememberThreshold(key, value) {
  const payload = JSON.stringify({ v: value, t: Date.now() });
  for (const store of [storageOrNull('localStorage'), storageOrNull('sessionStorage')]) {
    if (!store) continue;
    try {
      store.setItem(key, payload);
    } catch (e) {
      /* úložiště není k dispozici — zkusí se to druhé */
    }
  }
  return value;
}

function recallThreshold(key) {
  const store = storageOrNull('localStorage');
  const fromLocal = store ? readThreshold(store, key) : null;
  if (fromLocal) return fromLocal;
  try {
    const session = storageOrNull('sessionStorage');
    return session ? readThreshold(session, key) : null;
  } catch (e) {
    return null;
  }
}

// Název dárku nese jen nativní řádek tabulky (`cartInfo` má samé částky).
function giftName() {
  const el = document.querySelector('tr.free-gift-row .free-gift-name');
  const text = el ? el.textContent.trim() : '';
  return text || null;
}

function benefitsData() {
  const info = shoptetData('cartInfo');
  if (!info) return null;
  const price = info.getNoBillingShippingPrice;
  const goods = price ? Number(price.withVat) : NaN;
  if (!Number.isFinite(goods)) return null;

  // doprava zdarma — JEN z živých dat (freeShippingState, stejná čísla jako
  // modal na detailu); bez prahu se milník nekreslí (těžký košík, nadrozměr)
  const s = freeShippingState();
  const ship = s ? { from: s.shipFrom, reached: s.reached, leftText: s.leftText } : null;

  // dárek — `freeGift` je po dosažení POLE vybraných dárků, ne `true`
  const giftLeftRaw = cartLeft(info, 'leftToFreeGift');
  const giftRow = !!document.querySelector('tr.free-gift-row');
  const giftReached =
    giftRow ||
    info.freeGift === true ||
    (Array.isArray(info.freeGift) && info.freeGift.length > 0) ||
    (Number.isFinite(giftLeftRaw) && giftLeftRaw <= 0);
  const hasGift = Number.isFinite(giftLeftRaw) || giftReached;
  let giftAt = null;
  if (Number.isFinite(giftLeftRaw) && giftLeftRaw > 0) {
    giftAt = rememberThreshold(GIFT_KEY, goods + giftLeftRaw);
  } else if (giftReached) {
    giftAt = recallThreshold(GIFT_KEY);
    // zapamatovaný práh nesmí odporovat živému stavu (dárek už je, ale zboží
    // je pod prahem = promo se mezitím změnilo) → radši žádný milník než lež
    if (giftAt && goods < giftAt) giftAt = null;
  }
  const gift = hasGift
    ? { reached: giftReached, left: Number.isFinite(giftLeftRaw) && giftLeftRaw > 0 ? giftLeftRaw : 0, at: giftAt }
    : null;

  if (!gift && !ship) return null;
  return { goods: goods, ship: ship, gift: gift };
}

// Hlavička baru (prototyp v3): vlevo stav („Máte dárek i dopravu zdarma" /
// „Máte dárek zdarma" / „Vaše výhody k objednávce"), vpravo co chybí —
// nejdřív doprava, pak dárek, po dosažení obojího hodnota zboží.
function milestoneTexts(d) {
  const giftOk = !!(d.gift && d.gift.reached);
  const shipOk = !!(d.ship && d.ship.reached);
  let head = MS_HEAD_NONE;
  if (giftOk && shipOk) head = MS_HEAD_BOTH;
  else if (giftOk) head = MS_HEAD_GIFT;
  else if (shipOk) head = MS_HEAD_SHIP;
  let sub;
  if (d.ship && !shipOk && d.ship.leftText) {
    sub = L.msSubShip(d.ship.leftText.replace(/\s/g, NBSP));
  } else if (d.gift && !giftOk && priceText(d.gift.left)) {
    sub = L.msSubGift(priceText(d.gift.left));
  } else {
    sub = L.msSubGoods(priceText(d.goods) || withCurrency('0'));
  }
  return { head: head, sub: sub };
}

// Jeden společný bar (prototyp v3 §3): hlavička, škála 0 → max s výplní podle
// hodnoty zboží a dva milníky (dárek / doprava) s popisky pod škálou; splněný
// milník zeleně. Nahrazuje dřívější zelené pásy `.bs-band` per výhoda
// (2026-09-01) — ty zůstávají jen modalu „Možnosti doručení" na detailu.
function buildBenefits() {
  const d = benefitsData();
  if (!d) return null;
  const t = milestoneTexts(d);

  const root = node('div', 'bs-co-bene');
  const head = node('div', 'bs-co-bene__head');
  head.appendChild(node('span', 'bs-co-bene__title', t.head));
  head.appendChild(node('span', 'bs-co-bene__sub', t.sub));
  root.appendChild(head);
  // Co dárek je (review Tomáše 2026-09-09: v košíku chyběla informace, že
  // dárek = pracovní rukavice). Název nese jen nativní řádek tabulky, takže se
  // kreslí až po dosažení prahu — před ním jméno dárku nikde není.
  const gift = giftName();
  if (gift) {
    const line = node('div', 'bs-co-bene__gift');
    line.appendChild(icon('gift'));
    line.appendChild(node('span', 'bs-co-bene__gift-t', GIFT_LABEL + ': '));
    line.appendChild(node('span', 'bs-co-bene__gift-n', gift));
    root.appendChild(line);
  }
  // Akce na dopravu (#322, mock M11): košík s nárokem na dopravu zdarma (do
  // 10 kg — těžký košík milník dopravy nemá, `d.ship` null), který práh ještě
  // nedosáhl, platí do té doby akční cenu výdejního místa.
  const promo = d.ship && !d.ship.reached ? shippingPromo() : null;
  if (promo) {
    const line = node('div', 'bs-co-bene__gift bs-co-bene__promo');
    line.appendChild(icon('truck'));
    line.appendChild(node('span', 'bs-co-bene__gift-n', L.promoLine(promo.carrier, priceText(promo.price), promo.maxKg, promo.untilLabel)));
    root.appendChild(line);
  }

  // milník bez známého prahu (dárek dosažený bez zapamatované hodnoty) se
  // nekreslí — pozice by byla odhad
  const marks = [];
  if (d.gift && d.gift.at) {
    marks.push({ key: 'gift', at: d.gift.at, met: d.gift.reached, icon: 'gift', label: MS_LABEL_GIFT, sub: priceText(d.gift.at) });
  }
  if (d.ship) {
    marks.push({ key: 'ship', at: d.ship.from, met: d.ship.reached, icon: 'truck', label: MS_LABEL_SHIP, sub: priceText(d.ship.from) + MS_SHIP_KG });
  }
  const max = Math.max.apply(null, [d.goods].concat(marks.map((m) => m.at))) * MS_HEADROOM;
  const pct = (v) => (max > 0 ? Math.max(0, Math.min(100, (v / max) * 100)) : 0);

  const wrap = node('div', 'bs-co-bene__wrap');
  const bar = node('div', 'bs-co-bene__bar');
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-label', MS_BAR_LABEL);
  bar.setAttribute('aria-valuemin', '0');
  bar.setAttribute('aria-valuemax', String(Math.round(max)));
  bar.setAttribute('aria-valuenow', String(Math.round(Math.min(d.goods, max))));
  const fill = node('span', 'bs-co-bene__fill');
  fill.style.width = pct(d.goods) + '%';
  bar.appendChild(fill);
  for (const m of marks) {
    const state = m.met ? ' is-met' : '';
    const dot = node('span', 'bs-co-bene__ms bs-co-bene__ms--' + m.key + state);
    dot.style.left = pct(m.at) + '%';
    dot.setAttribute('aria-hidden', 'true');
    dot.appendChild(icon(m.icon));
    bar.appendChild(dot);
    const lab = node('span', 'bs-co-bene__lab bs-co-bene__lab--' + m.key + state);
    lab.style.left = pct(m.at) + '%';
    lab.appendChild(node('span', 'bs-co-bene__lab-t', m.label));
    lab.appendChild(node('span', 'bs-co-bene__lab-s', m.sub));
    bar.appendChild(lab);
  }
  wrap.appendChild(bar);
  root.appendChild(wrap);
  return root;
}

// odstranit + znovu postavit (běží jen v dekoračních průchodech, observery
// jsou v tu chvíli odpojené)
function renderBenefits(parent, before) {
  const old = parent.querySelector(':scope > .bs-co-bene');
  if (old) old.remove();
  const bene = buildBenefits();
  if (bene) parent.insertBefore(bene, before || null);
}

// --- fotky položek pro rekapitulaci ----------------------------------------
// Nativní `#summary-box` v krocích 2–3 obrázky nemá — mapa {guid: src} se
// ukládá v košíku a čte v dalších krocích. Nedojede-li (přímý vstup, jiná
// session), thumb se vykreslí jako prázdný rámeček s qty badge.
const THUMBS_KEY = 'bsCoThumbs';

function thumbsStore(map) {
  try {
    sessionStorage.setItem(THUMBS_KEY, JSON.stringify(map));
  } catch (e) {
    /* tichá degradace */
  }
}

function thumbsLoad() {
  try {
    return JSON.parse(sessionStorage.getItem(THUMBS_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

// --- krok 1: košík ---------------------------------------------------------

// Panel souvisejících patří ke KONKRÉTNÍ položce (`tr.related.related-to-<priceId>`).
// Toggle se stěhuje z buňky názvu do vlastního řádku pod položkou, proto si
// pamatuje svůj řádek položky (po přesunu už `closest('tr')` není on).
const TOGGLE_ROW = new WeakMap();

function relatedRowFor(row) {
  if (!row) return null;
  const priceId = row.querySelector('input[name="priceId"]');
  if (priceId && priceId.value) {
    const paired = document.querySelector('tr.related.related-to-' + priceId.value);
    if (paired) return paired;
  }
  let next = row.nextElementSibling;
  while (next && !next.classList.contains('related')) {
    if (next.classList.contains('removeable')) return null;
    next = next.nextElementSibling;
  }
  return next && next.classList.contains('related') ? next : null;
}

/**
 * Konec akční ceny pod jednotkovou cenou položky (saleEnd.js). Nativní řádek
 * košíku přeškrtnutou cenu NEMÁ, takže slevu dokládá dataLayer
 * (`priceWithoutDiscount > priceWithVat` při `finalRatio` 1 — jiná sleva,
 * kupón nebo množstevní, konec akce neznamená); konec dává index (`/specs`).
 * Idempotentní per řádek — AJAX košíku řádky vyměňuje, nové projdou znovu.
 */
function decorateSaleEnds(rows) {
  const info = shoptetData('cartInfo');
  const items = info && Array.isArray(info.cartItems) ? info.cartItems : [];
  const byCode = new Map();
  for (const row of rows) {
    if (row.dataset.bsSale) continue;
    const cell = row.querySelector('td.p-price');
    const code = row.getAttribute('data-micro-sku');
    if (!cell || !code) continue;
    row.dataset.bsSale = '1';
    const item = items.find(function (it) {
      return it && it.code === code;
    });
    if (!item) continue;
    const ratio = item.discounts ? Number(item.discounts.finalRatio) : 1;
    const discounted = Number(item.priceWithoutDiscount) > Number(item.priceWithVat) && (!isFinite(ratio) || ratio === 1);
    if (!discounted) continue;
    if (!byCode.has(code)) byCode.set(code, []);
    byCode.get(code).push(cell);
  }
  if (!byCode.size) return;
  saleEndsFor([...byCode.keys()]).then(function (ends) {
    ends.forEach(function (toDate, code) {
      if (!toDate) return;
      for (const cell of byCode.get(code) || []) {
        if (!cell.isConnected || cell.querySelector('.bs-co-item__sale')) continue;
        const el = createSaleEnd(toDate, { className: 'bs-co-item__sale' });
        if (el) cell.appendChild(el);
      }
    });
  });
}

/**
 * Původní cena a procento slevy u položky košíku (zadání 2026-08-31, od v3
 * 2026-09-02 ve sloupci SOUČTU nad řádkovou cenou): nativní řádek nese slevu
 * jen v tooltipu „Již obsahuje slevu … ze standardní ceny", který v redesignu
 * není vidět. Přeškrtnutá cena = běžná cena × množství (prototyp „1 798 Kč"
 * u 2 ks), procento z JEDNOTKOVÝCH cen dataLayeru (`priceWithoutDiscount` /
 * `priceWithVat`) a sdílí floor pravidlo Shoptetu (`productPrice.discountPercent`).
 * Řádek se při každém průchodu přepíše (množství se mění AJAXem), sloupec
 * ceny za kus zůstává holé „799 Kč / ks" (+ případný konec akce).
 */
function decorateItemDiscounts(rows) {
  const info = shoptetData('cartInfo');
  const items = info && Array.isArray(info.cartItems) ? info.cartItems : [];
  for (const row of rows) {
    const cell = row.querySelector('td.p-total');
    const code = row.getAttribute('data-micro-sku');
    if (!cell || !code) continue;
    const item = items.find(function (it) {
      return it && it.code === code;
    });
    const was = item ? Number(item.priceWithoutDiscount) : NaN;
    const unit = item ? Number(item.priceWithVat) : NaN;
    let line = cell.querySelector(':scope > .bs-co-item__was');
    if (!(Number.isFinite(was) && Number.isFinite(unit) && was > unit)) {
      if (line) line.remove();
      continue;
    }
    if (!line) {
      line = node('span', 'bs-co-item__was');
      line.appendChild(node('s'));
      line.appendChild(node('b'));
      // POD řádkovou cenou (review Tomáše 2026-09-09): hlavní částka musí
      // stát v rovině s ostatními prvky řádku, sleva až pod ní
      const fin = cell.querySelector('.price-final');
      if (fin) fin.insertAdjacentElement('afterend', line);
      else cell.appendChild(line);
    }
    const qty = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
    setText(line.querySelector('s'), priceText(was * qty));
    const pct = discountPercent(was, unit);
    setText(line.querySelector('b'), pct != null ? '−' + pct + NBSP + '%' : '');
    line.querySelector('b').hidden = pct == null;
  }
}

// Úspora z běžných cen přes celý košík (jednotkový rozdíl × množství) a její
// podíl na běžných cenách (v3 §7.3: položky bez běžné ceny se počítají
// s běžná = prodejní, doprava a platba do procent nejdou, zaokrouhleno na celá).
// Kupón do součtu nepatří — dataLayer ho nese v `discounts.couponRatio`
// a nativní souhrn mu kreslí vlastní řádek.
//
// Velkoobchod (wholesalePrice.js): položka BEZ nativní slevy, jejíž VO cena je
// pod běžnou i pod cenou nepřihlášeného, bere jako běžnou cenu tu z indexu —
// úspory VO položek a akčních položek se sčítají do jednoho pásku (rozhodnutí
// Tomáše 2026-09-13), žádná položka se nepočítá dvakrát. Výprodejová položka
// (VO = akce, sleva ceníku se neuplatnila) se počítá jako akční (2026-09-14). Běžné ceny se
// dotahují asynchronně; než dorazí, souhrn jede bez nich a po doběhnutí se
// překreslí (`refreshTotals`). `wholesale` = kontext ceníku, jen když VO
// úspora v součtu opravdu je.
function cartSavings() {
  const info = shoptetData('cartInfo');
  const items = info && Array.isArray(info.cartItems) ? info.cartItems : [];
  const vo = wholesaleCustomer(shoptetData('customer'));
  if (vo) {
    ensurePrices(
      items.map(function (it) {
        return it && it.code;
      }),
      refreshTotals,
    );
  }
  let saved = 0;
  let list = 0;
  let voSaved = 0;
  for (const it of items) {
    const unit = Number(it && it.priceWithVat);
    if (!Number.isFinite(unit)) continue;
    const wasRaw = Number(it && it.priceWithoutDiscount);
    const native = Number.isFinite(wasRaw) && wasRaw > unit;
    const qty = Number(it && it.quantity) || 1;
    let was = native ? wasRaw : unit;
    if (vo && !native) {
      const adv = wholesaleAdvantage(knownPrices(it.code), unit);
      if (adv) {
        was = adv.mo;
        // výprodej (`sale_blocked`): úspora je akční, ne z ceníku — do součtu
        // patří, VO pásek sama nezapne
        if (adv.state === 'vo') voSaved += (was - unit) * qty;
      }
    }
    saved += (was - unit) * qty;
    list += was * qty;
  }
  if (!(saved > 0) || !(list > 0)) return null;
  return { saved: saved, list: list, percent: Math.round((saved / list) * 100), wholesale: voSaved > 0 ? vo : null };
}

// Po dotažení běžných cen velkoobchodu překreslí bloky „Celkem k úhradě"
// (decorateTotal je idempotentní; souhrn kroku 1 i rekapitulace kroků 2–3).
function refreshTotals() {
  document.querySelectorAll('.bs-co-sum__total').forEach(decorateTotal);
}

// Řádek „1 798 Kč −11 %" nad cenou položky v rekapitulaci (v3 §7.1): běžná
// cena × ks z JEDNOTKOVÝCH cen dataLayeru, procento floor jako u položky
// košíku (`productPrice.discountPercent`, pravidlo Shoptetu). Bez běžné ceny null.
function sumWasRow(item, qty) {
  if (!item) return null;
  const was = Number(item.priceWithoutDiscount);
  const unit = Number(item.priceWithVat);
  if (!(Number.isFinite(was) && Number.isFinite(unit) && was > unit)) return null;
  const n = Number(item.quantity) > 0 ? Number(item.quantity) : qty > 0 ? qty : 1;
  const line = node('span', 'bs-co-sum__was');
  line.appendChild(node('s', null, priceText(was * n)));
  const pct = discountPercent(was, unit);
  if (pct != null) line.appendChild(node('b', null, '−' + pct + NBSP + '%'));
  return line;
}

// „2 396 Kč" / „1 857,02 Kč" → číslo (`\s` bere i pevnou mezeru, desetinná čárka)
function parsePrice(text) {
  const m = String(text || '')
    .replace(/\s/g, '')
    .match(/-?\d+(?:[.,]\d+)?/);
  return m ? parseFloat(m[0].replace(',', '.')) : null;
}

// Řádek doplňkové služby do podoby prototypu: checkbox | název (+ „?") + popis | cena.
// Popis = text nativního `<details>` „Informace o službě", které se ruší; text jde
// pod název jako `.bs-co-svc__desc` a CSS ho na desktopu KRÁTÍ na jeden řádek
// s výpustkou, na mobilu ho skrývá úplně (rozhodnutí Tomáše 2026-09-11 nad živým
// screenshotem: rozepsaný popis zabíral na telefonu půl obrazovky, na desktopu
// chce aspoň zkrácenou verzi). Plný text nese tooltip medailonku „?" (Bootstrap
// tooltip doplňku, `data-original-title`). Název a medailonek dostávají společný
// obal kvůli mřížce. Idempotentní (observer i opakovaný initCart běží vícekrát).
function decorateServices(fv) {
  for (const prod of fv.querySelectorAll('.fvDoplnek-produkt')) {
    const text = prod.querySelector('.fvDoplnek-text');
    if (!text) continue;
    const name = text.querySelector('.name');
    if (name && !text.querySelector('.bs-co-svc__title')) {
      const title = node('span', 'bs-co-svc__title');
      name.insertAdjacentElement('beforebegin', title);
      title.appendChild(name);
      const tip = text.querySelector('.upsell-product-tooltip');
      if (tip) title.appendChild(tip);
    }
    const det = prod.querySelector('details.fvDoplnek-accordion');
    const body = prod.querySelector('.fvDoplnek-accordion__content');
    if (body && !body.classList.contains('bs-co-svc__desc')) {
      body.classList.add('bs-co-svc__desc');
      const price = text.querySelector('.price');
      if (price) price.insertAdjacentElement('beforebegin', body);
      else text.appendChild(body);
    }
    if (det) det.remove();
    // cena jako příplatek: „+ 20 Kč" (prototyp); pevná mezera před jednotkou
    const price = text.querySelector('.price');
    if (price && !/^\+/.test((price.textContent || '').trim())) {
      setText(price, '+ ' + (price.textContent || '').trim().replace(/\s+/g, NBSP));
    }
  }
}

function initCart() {
  const inner = document.querySelector('.cart-inner');
  if (!inner || inner.classList.contains('cart-empty')) return;
  const wrap = document.getElementById('cart-wrapper');
  if (wrap) wrap.classList.add('bs-co-cart');
  inner.classList.add('bs-co-grid', 'bs-co-cart__inner');
  const table = document.querySelector('table.cart-table');
  if (table) table.classList.add('bs-co-tbl');
  // termín: dekorace + uložení pro krok 2 (uzel po AJAXu mizí i vzniká)
  initCartEta();

  const rows = table ? table.querySelectorAll('tr.removeable') : [];

  // levý sloupec = jedna karta košíku (hlavička · položky · termín · progress
  // · zpět do obchodu); pravý sloupec staví initSummary('cart')
  let main = inner.querySelector(':scope > .bs-co-main');
  if (!main) {
    main = node('div', 'bs-co-main');
    inner.insertBefore(main, inner.firstChild);
  }
  let card = main.querySelector(':scope > .bs-co-card--cart');
  if (!card) {
    card = node('div', 'bs-co-card--cart');
    main.appendChild(card);
  }

  // hlavičkový pás karty: nativní H1 je `sr-only` mimo kartu → přesun + počet
  let head = card.querySelector(':scope > .bs-co-cart__head');
  if (!head) {
    head = node('div', 'bs-co-cart__head');
    const h1 = document.querySelector('#content .content-inner h1.sr-only, #content > h1.sr-only');
    if (h1) {
      h1.classList.remove('sr-only');
      h1.classList.add('bs-co-cart__title');
      head.appendChild(h1);
    } else {
      head.appendChild(node('h1', 'bs-co-cart__title', L.cartTitle));
    }
    head.appendChild(node('span', 'bs-co-cart__count'));
  }
  setText(head.querySelector('.bs-co-cart__count'), itemsWord(rows.length));
  card.appendChild(head);
  if (table) card.appendChild(table);

  // Doplňkový prodej fvStudio (jen ostrá): doplněk vkládá `.fvDoplnek-upsell`
  // za `.cart-table` na `$(document).ready` — podle pořadí bootů to může být
  // PŘED i PO našem přesunu tabulky do karty. Blok mimo kartu by spadl do grid
  // kontejneru splitu jako vlastní sloupec → přestěhovat ho do VLASTNÍ karty
  // „Volitelné doplňkové služby" POD kartou košíku (prototyp v2a, sekce
  // `data-screen-label="Doplňkové služby"`).
  // Nativní `<input type=checkbox>` i jeho obal se PŘESOUVAJÍ (appendChild) —
  // klon by ztratil handler doplňku (`fvStudioAppCartUpsell.addToCart`).
  // jQuery event `fvstudioCartUpsellLoaded` k nativnímu listeneru nedoletí,
  // pozdní vložení chytá observer (zápis jen podmíněně — smyčka by se jinak
  // roztočila, viz memory playwright-file-url-a-observer-loop).
  const adoptUpsell = () => {
    // po AJAX překreslení drží closure staré uzly — nechat práci novému běhu
    if (!card.isConnected || !main.isConnected) return;
    const fv = document.querySelector('#cart-wrapper .fvDoplnek-upsell, .cart-inner .fvDoplnek-upsell');
    if (!fv) return;
    let svc = main.querySelector(':scope > .bs-co-card--services');
    if (!svc) {
      svc = node('div', 'bs-co-card--services');
      const head = node('div', 'bs-co-services__head');
      head.appendChild(node('h2', 'bs-co-services__title', SERVICES.title));
      head.appendChild(node('span', 'bs-co-services__note', SERVICES.note));
      svc.appendChild(head);
    }
    // srovnává se POZICE, ne jen rodič: opakovaný initCart re-appenduje kartu
    // košíku na konec `main` a dřív vyrobená karta služeb by zůstala první
    if (svc.previousElementSibling !== card) main.appendChild(svc);
    if (fv.parentElement !== svc) svc.appendChild(fv);
    decorateServices(fv);
  };
  adoptUpsell();
  // Observer vzniká jednou, ale volá VŽDY adopci z posledního běhu initCart —
  // closure prvního běhu by po AJAX výměně držela odpojené uzly a pozdní
  // vložení bloku doplňkem by nechala mimo kartu (nález B1-06).
  if (wrap) {
    wrap._bsAdoptUpsell = adoptUpsell;
    if (!wrap.dataset.bsCoFvWatch) {
      wrap.dataset.bsCoFvWatch = '1';
      new MutationObserver(function () {
        if (wrap._bsAdoptUpsell) wrap._bsAdoptUpsell();
      }).observe(wrap, { childList: true, subtree: true });
    }
  }

  const thumbs = {};
  for (const row of rows) {
    // mapa fotek pro rekapitulaci kroků 2–3 — sbírá se při každém průchodu
    // (AJAX může položky přidat i ubrat)
    const guid = row.getAttribute('data-micro-identifier');
    const img = row.querySelector('td.cart-p-image img');
    if (guid && img && img.src) thumbs[guid] = img.src;

    if (row.dataset.bsCo) continue;
    row.dataset.bsCo = '1';
    row.classList.add('bs-co-item');

    // × = mazací form v buňce součtu (prototyp: 86px součet | 34px ×); duplicitní
    // form u názvu skrývá CSS
    const del = row.querySelector('td.p-total form') || row.querySelector('td.p-name form.delete-cart-item');
    if (del) {
      del.classList.add('bs-co-item__del');
      const btn = del.querySelector('button.remove-item');
      if (btn && !btn.querySelector('.bs-icon')) {
        btn.appendChild(icon('close'));
        btn.setAttribute('title', L.removeItem);
      }
    }

    // `.main-link` je u běžné položky odkaz, u položky služby (fvStudio) SPAN
    const name = row.querySelector('td.p-name .main-link');
    if (name) name.classList.add('bs-co-item__name');
    const avail = row.querySelector('.product-table-name-footer');
    if (avail) {
      avail.classList.add('bs-co-item__avail');
      // Shoptet sází barvu dostupnosti INLINE z administrace → řídí ji token.
      const label = avail.querySelector('.availability-label');
      if (label) label.style.removeProperty('color');
    }

    // související produkty: nativně nezabalené (blank mode) → sbalit za toggle;
    // toggle jde do vlastního řádku pod položku (prototyp: řádek „Související
    // produkty (N)" mezi položkami a termínem)
    const toggle = row.querySelector('a.show-related');
    const related = relatedRowFor(row);
    if (toggle) {
      toggle.classList.add('bs-co-item__rel-toggle');
      toggle.removeAttribute('aria-hidden');
      toggle.removeAttribute('tabindex');
      const count = toggle.querySelector('b');
      toggle.textContent = RELATED_MORE + (count ? ' (' + count.textContent.trim() + ')' : '');
      toggle.appendChild(icon('chevron-down'));
      TOGGLE_ROW.set(toggle, row);
      const relrow = node('tr', 'bs-co-relrow');
      relrow.appendChild(node('td'));
      row.parentNode.insertBefore(relrow, row.nextSibling);
      relrow.firstChild.appendChild(toggle);
    }
    if (related) related.classList.add('bs-co-rel');
  }
  thumbsStore(thumbs);
  decorateSaleEnds(rows);
  decorateItemDiscounts(rows);

  for (const toggle of document.querySelectorAll('a.show-related.bs-co-item__rel-toggle')) {
    if (toggle.dataset.bsCoBound) continue;
    toggle.dataset.bsCoBound = '1';
    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      // panel se hledá k VLASTNÍMU řádku (jinak se při více položkách rozbalí cizí)
      const target = relatedRowFor(TOGGLE_ROW.get(toggle) || toggle.closest('tr'));
      if (!target) return;
      const open = target.classList.toggle('is-open');
      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    toggle.setAttribute('aria-expanded', 'false');
  }

  // „Hodí se k tomu" — 1–2 kusy příslušenství pod položkou (cartCrossSell.js),
  // z téhož nativního panelu souvisejících; řádek jde mezi položku a toggle
  renderCartCrossSell(rows, relatedRowFor);

  // Dárek k objednávce: nativní řádek tabulky se SKRÝVÁ (CSS) a v souhrnu ho
  // nahradí řádek „Dárek: … — Zdarma" dle prototypu (README §79, rozhodnutí
  // Tomáše 2026-08-21). Uzel zůstává v DOM — je jediným zdrojem názvu dárku
  // pro souhrn i pro zprávu v progress liště.
  const gift = document.querySelector('tr.free-gift-row');
  if (gift) gift.classList.add('bs-co-gift');

  // zelený pás termínu + progress výhod patří do karty (pod tabulku)
  const eta = document.querySelector('.delivery-time');
  if (eta) card.appendChild(eta);
  renderBenefits(card, null);

  // spodek karty: nativní „Zpět do obchodu" → „← Pokračovat v nákupu"
  const back = document.querySelector('.next-step--cart .next-step-back');
  let foot = card.querySelector(':scope > .bs-co-cart__foot');
  if (!foot) {
    foot = node('div', 'bs-co-cart__foot');
  }
  card.appendChild(foot);
  if (back && !back.dataset.bsCo) {
    back.dataset.bsCo = '1';
    back.classList.add('bs-co-cart__back');
    const label = back.querySelector('.continue-shopping') || back;
    label.textContent = CONTINUE_SHOPPING;
    back.insertBefore(icon('arrow-left'), back.firstChild);
  }
  if (back) foot.appendChild(back);

  // nativní motivační řádky (`.extra`) i jejich box skrývá CSS — nahrazuje je
  // progress výhod; `.row.summary` zbyde jako prázdná skořápka (CSS display:none)
  const sumRow = inner.querySelector(':scope > .row.summary');
  if (sumRow) sumRow.classList.add('bs-co-sumrow');
}

function initEmptyCart() {
  const empty = document.querySelector('.cart-inner.cart-empty');
  if (!empty || empty.dataset.bsCo) return;
  empty.dataset.bsCo = '1';
  empty.classList.add('bs-co-empty');

  const heading = empty.querySelector('.cart-heading');
  const badge = node('span', 'bs-co-empty__ic');
  badge.appendChild(icon('cart'));
  if (heading) {
    heading.classList.add('bs-co-empty__title');
    heading.textContent = EMPTY.title;
    heading.insertAdjacentElement('beforebegin', badge);
    const text = node('p', 'bs-co-empty__text', EMPTY.text);
    heading.insertAdjacentElement('afterend', text);
    const cta = link('/', 'bs-co-empty__cta', EMPTY.cta);
    cta.appendChild(icon('arrow-right'));
    text.insertAdjacentElement('afterend', cta);
  }

  const boxes = empty.querySelector('.empty-cart-boxes');
  if (boxes) boxes.classList.add('bs-co-empty__boxes');
  const search = empty.querySelector('.js-try-search-button');
  if (search) search.classList.add('bs-co-empty__search');

  for (const box of empty.querySelectorAll('.col-md-4 .cart-content > .box')) {
    box.classList.add('bs-co-help');
  }
}

// --- krok 2: doprava a platba ---------------------------------------------

// Programová volba dopravy/platby MUSÍ jít přes `mousedown` na wrapperu.
// Shoptet věší přepočet na `mousedown` každého `.radio-wrapper` (checkout-3g.js)
// a zdrojem pravdy pro cenu je třída `.active`, NE `input:checked`. `input.click()`,
// `label.click()`, `change` dispatch ani jQuery `.trigger()` ho nespustí.
// Stejný dispatch používá sám Shoptet ve `setPickupPoint()`. (Nález B1, 2026-08-20.)
function selectNativeMethod(wrapper) {
  if (!wrapper) return;
  // U skupiny s výdejním místem otevírá nativní handler rovnou widget dopravce.
  // Při volbě celé skupiny to nechceme — `chosen` ho na dobu dispatche umlčí.
  const choose = wrapper.querySelector('.new-widget-choose a, .pickup-point-choose a, .new-gen-choose a');
  const muted = choose && !choose.classList.contains('chosen');
  if (muted) choose.classList.add('chosen');
  try {
    wrapper.dispatchEvent(new Event('mousedown'));
  } finally {
    if (muted) choose.classList.remove('chosen');
  }
  // Když `.active` nepřibylo, nikdo `mousedown` neposlouchá (offline fixture bez
  // default JS) — dorovnáme aspoň `checked`; živě tahle větev nenastane.
  if (!wrapper.classList.contains('active')) {
    const input = wrapper.querySelector('input[type="radio"]');
    if (input) input.checked = true;
  }
}

// Most pro nativní přepnutí, které neprošlo `mousedown` (šipky, autofill) —
// nativní výběr klávesnicí je bez něj rozbitý (přepne `checked`, cenu ne).
// Guard na `.active` drží skutečný klik myší mimo (druhý dispatch = dvojí přepočet).
function bridgeNativeChange(wrapper) {
  if (wrapper && !wrapper.classList.contains('active')) selectNativeMethod(wrapper);
}

function classifyShipping(wrapper) {
  if (wrapper.querySelector('.personal-collection-point-id')) return 'pickup';
  // čistý název — bez naší nápovědy i suffixu pásma (shipMethodName)
  return shippingGroupKey(shipMethodName(wrapper) || '');
}

// Nadrozměrné metody („PPL – Nadrozměrné zásilky", „Fofr – Nadměrné balíky")
// nabízí Shoptet každému košíku ve svém hmotnostním rozsahu — pro běžné zboží
// jsou jen dražší duplikát dopravy na adresu (rozhodnutí Tomáše 2026-09-02,
// pravidlo sdílené s modalem: shippingModel.js withoutRedundantOversize).
// Je-li v nabídce aspoň jedna běžná doručovaná metoda, nadrozměrné wrappery
// dostanou třídu (CSS display:none) a do karet skupin se nedostanou; nativní
// uzel v tabulce zůstává, radio se odesílá s formulářem. Atypický košík
// (jen nadrozměrné metody) i košík nad limity všech běžných metod zůstávají
// beze změny. Idempotentní — volá se i ze sync() po AJAXu, kdy default JS
// wrappery vyměňuje (nový uzel třídu nemá).
const OVERSIZE_HIDDEN = 'bs-co-opt--redundant';

function isOversizeWrapper(wrapper) {
  return OVERSIZE_RE.test(shipMethodName(wrapper) || '');
}

function hideRedundantOversize(table) {
  const wrappers = Array.prototype.slice.call(table.querySelectorAll('.radio-wrapper'));
  const standard = wrappers.some(function (w) {
    const key = classifyShipping(w);
    return (key === 'box' || key === 'address') && !isOversizeWrapper(w);
  });
  if (!standard) return wrappers;
  return wrappers.filter(function (w) {
    const redundant = isOversizeWrapper(w);
    if (redundant && !w.classList.contains(OVERSIZE_HIDDEN)) w.classList.add(OVERSIZE_HIDDEN);
    return !redundant;
  });
}

// Název metody bez naší inline nápovědy („?" vkládá `inlineTooltip` DOVNITŘ
// názvu, do textu nepatří) — na čistém názvu stojí classifyShipping i skrytí
// nadrozměru, každý další náš span do názvu sem musí přibýt taky. (Suffix
// pásma „– zásilka 7–15 kg" za názvem od v3 2026-09-02 není.)
function shipMethodName(wrapper) {
  const el = wrapper.querySelector('.shipping-billing-name');
  if (!el) return null;
  const parts = [];
  for (const child of el.childNodes) {
    // nápověda „?" i náš štítek („Nejrychlejší") do názvu nepatří — na čistém
    // názvu stojí classifyShipping i skrytí nadrozměru
    if (child.nodeType === 1 && (child.classList.contains('question-tooltip') || child.classList.contains('bs-co-opt__tag'))) continue;
    parts.push(child.textContent || '');
  }
  const text = parts.join(' ').replace(/\s+/g, ' ').trim();
  return text || null;
}

// Štítek v hlavičce karty dopravy (v3 §4.1): ikona váhy + „Zásilka 8,5 kg" —
// pásmo tu od v3 není, nese ho škála pod skupinami. Bez váhy v datech se
// nekreslí.
function renderWeightChip(head) {
  // plochý tarif hmotnost nekomunikuje — cena je jedna bez ohledu na váhu
  const cb = HAS_WEIGHT_BANDS ? cartBand() : null;
  let chip = head.querySelector('.bs-co-ship__chip');
  if (!cb) {
    if (chip) chip.remove();
    return;
  }
  if (!chip) {
    chip = node('span', 'bs-co-box__meta bs-co-ship__chip');
    chip.appendChild(icon('scale'));
    chip.appendChild(node('span', 'bs-co-ship__chip-txt'));
    head.appendChild(chip);
  }
  setText(chip.querySelector('.bs-co-ship__chip-txt'), WEIGHT_CHIP_LEAD + formatKg(cb.kg));
}

// Škála hmotnosti pod skupinami (v3 §4.2, nahrazuje větnou patičku i suffixy
// za názvy metod). Markup i vzhled dodává sdílený `weightScale.js` — od
// handoveru „Doprava a platba v2" kreslí týž prvek i stránka /doprava-platba/,
// takže čísla v košíku a v ceníku nemůžou být jiná. Pokladna rozhoduje jen
// o tom, KDY se škála kreslí a co je v poznámce: bez váhy v datech nebo bez
// běžné doručované metody v nabídce (jen odběr / jen atypická přeprava — ceny
// škály by neplatily) nic; poznámka o dopravě zdarma jen když jsou v nabídce
// výdejní místa (bez nich by slibovala metodu, která v objednávce není —
// rozhodnutí 2026-08-25), nad posledním pásmem místo ní věta o nadměrných
// balících.
function renderWeightScale(table, opts) {
  const cb = HAS_WEIGHT_BANDS ? cartBand() : null;
  let el = table.querySelector(':scope > .bs-co-wscale');
  if (!cb || !opts.hasDelivery) {
    if (el) el.remove();
    return;
  }
  const sig = JSON.stringify([cb.kg, cb.band.key, opts.hasBox]);
  // zápis jen při změně — volá se i ze sync() po AJAXu
  if (el && el.dataset.bsSig === sig) return;

  const note = cb.band.over
    ? WEIGHT_FOOT_OVER
    : opts.hasBox
      ? WEIGHT_SCALE_NOTE_A + freeShipFromText() + '.'
      : null;
  const next = buildWeightScale({ kg: cb.kg, band: cb.band, note: note });
  next.dataset.bsSig = sig;
  if (el) el.replaceWith(next);
  else table.appendChild(next);
}

function groupPriceLabel(prices) {
  const valid = prices.filter(function (p) {
    return typeof p === 'number' && Number.isFinite(p);
  });
  if (!valid.length) return null;
  const min = Math.min.apply(null, valid);
  if (min <= 0) return { text: L.free, free: true };
  const text = priceText(min) || withCurrency(String(min));
  return { text: valid.length > 1 ? L.priceFrom(text) : text, free: false };
}

// Blok osobního odběru v kartě skupiny (zadání 2026-08-31, sdílené texty
// v shippingModel.js): stav otevřeno/zavřeno s nejbližším termínem vyzvednutí,
// poznámka o okamžitém odběru skladového zboží, adresa + mapa (nové okno).
// Stav se obnovuje v sync() průchodech (přelom otevírací doby).
function renderPickupStatus(el) {
  const s = pickupStatus(new Date());
  el.classList.toggle('is-open', s.open);
  const label = el.querySelector('.bs-co-pickup__slabel');
  if (label) setText(label, s.text);
}

// Kompletní otevírací doba pod adresou (zadání Tomáše 2026-08-31) — táž
// mechanika i tvar jako v modalu „Možnosti doručení" na detailu produktu.
function buildPickupHours() {
  const row = node('span', 'bs-co-pickup__row bs-co-pickup__hours-row');
  row.appendChild(icon('clock'));

  const box = node('span', 'bs-co-pickup__hours');
  box.appendChild(node('span', 'bs-co-pickup__hlabel', PICKUP_HOURS_LABEL));
  const list = node('span', 'bs-co-pickup__hlist');
  for (const h of openingHours(new Date())) {
    const item = node('span', 'bs-co-pickup__hrow' + (h.today ? ' is-today' : '') + (h.open ? '' : ' is-closed'));
    item.appendChild(node('span', 'bs-co-pickup__hday', h.label));
    item.appendChild(node('span', 'bs-co-pickup__hval', h.value));
    list.appendChild(item);
  }
  box.appendChild(list);
  row.appendChild(box);
  return row;
}

function buildPickupInfo() {
  const box = node('div', 'bs-co-pickup');

  const status = node('span', 'bs-co-pickup__row bs-co-pickup__status');
  status.appendChild(node('span', 'bs-co-pickup__dot'));
  status.appendChild(node('span', 'bs-co-pickup__slabel'));
  box.appendChild(status);
  renderPickupStatus(status);

  const stock = node('span', 'bs-co-pickup__row');
  stock.appendChild(icon('check'));
  stock.appendChild(node('span', null, PICKUP_STOCK_NOTE));
  box.appendChild(stock);

  const addr = node('span', 'bs-co-pickup__row');
  addr.appendChild(icon('pin'));
  const addrTxt = node('span', null, PICKUP_ADDRESS + ' · ');
  const map = link(PICKUP_MAP_URL, 'bs-co-pickup__map', PICKUP_MAP_LABEL);
  map.target = '_blank';
  map.rel = 'noopener noreferrer';
  addrTxt.appendChild(map);
  addr.appendChild(addrTxt);
  box.appendChild(addr);

  box.appendChild(buildPickupHours());
  return box;
}

// Řádek skupiny: radio | titulek+popis | loga | termín | cena | chevron
// (handover §Krok 2). Skupina s jedinou metodou bez widgetu = přímá volba
// hlavním řádkem, bez chevronu a bez podvýběru — seznam se u ní nekreslí ani
// po vybrání (zadání Tomáše 2026-08-31: podvýběr s jedinou variantou mátl;
// týká se osobního odběru i nabídky zúžené na nadrozměrnou přepravu). Nativní
// `.radio-wrapper` v DOM zůstává (nese radio odesílané s formulářem), jen ho
// CSS schová — nápověda „?" se proto stěhuje na hlavní řádek.
function buildShipGroup(def, wrappers, table) {
  const single =
    wrappers.length === 1 && !wrappers[0].querySelector('.new-widget-choose, .pickup-point-choose');
  const card = node('div', 'bs-co-grp' + (single ? ' bs-co-grp--single' : ''));
  card.dataset.bsCoGroup = def.key;

  const head = node('button', 'bs-co-grp__head');
  head.type = 'button';
  head.setAttribute('aria-expanded', 'false');

  const radio = node('span', 'bs-co-grp__radio');
  radio.setAttribute('aria-hidden', 'true');
  head.appendChild(radio);

  // medailonek skupiny (mock 2026-09-02: výdejní místo / adresa / prodejna
  // rozeznatelné na první pohled; vybraná skupina červeně) — vědomá odchylka
  // od prototypu pokladny, checkout-parity ji neměří
  const ico = node('span', 'bs-co-grp__ico');
  ico.setAttribute('aria-hidden', 'true');
  ico.appendChild(icon(def.icon));
  head.appendChild(ico);

  const body = node('span', 'bs-co-grp__body');
  const titlerow = node('span', 'bs-co-grp__titlerow');
  const title = node('span', 'bs-co-grp__title', def.title);
  titlerow.appendChild(title);
  body.appendChild(titlerow);
  // Skupina s jedinou metodou má seznam voleb skrytý → zákazník by se jméno
  // dopravce nedozvěděl (u nadrozměru je to jediná informace, proč mu zmizely
  // ostatní možnosti). Osobní odběr si nechává adresu prodejny.
  const soleName = single && def.key !== 'pickup' ? shipMethodName(wrappers[0]) : null;
  const desc = node('span', 'bs-co-grp__desc', soleName || def.desc);
  body.appendChild(desc);
  // Nápověda metody („?") je u jediné metody jediný popis, který platforma
  // nese — se skrytým seznamem by zanikla, proto jde na hlavní řádek. Patří
  // DOVNITŘ titulku, ne vedle něj: jako sourozenec ve flexu se na mobilu
  // odlomila pod dlouhý název a visela tam na vlastním řádku sama.
  if (single) {
    const tip = wrappers[0].querySelector('.question-tooltip');
    if (tip) title.appendChild(tip);
  }
  head.appendChild(body);

  // logotypy dopravců v pevném sloupci (chipy). Přednost má NAŠE vektorové logo
  // ze spritu (logo.js) podle názvu metody; nativní obrázek Shoptetu (101×50 px,
  // GLS dokonce PNG) zůstává fallbackem pro dopravce, kterého sprite nezná —
  // originály musí v obou případech zůstat u možností. Osobní odběr logo
  // NEDOSTÁVÁ — je to značka shopu a mezi dopravci nic neříká (zadání Tomáše
  // 2026-08-31).
  const logos = node('span', 'bs-co-grp__logos');
  let logoCount = 0;
  const seen = {};
  for (const w of def.key === 'pickup' ? [] : wrappers) {
    if (logoCount >= 3) break;
    const mark = carrierLogo(shipMethodName(w));
    const img = w.querySelector('.payment-logo img');
    const key = mark ? 'vector:' + carrierLogoId(shipMethodName(w)) : img && img.src;
    if (!key || seen[key]) continue;
    if (!mark && !(img && img.src)) continue;
    seen[key] = true;
    const chip = node('span', 'bs-co-grp__logochip' + (mark ? ' is-vector' : ''));
    if (mark) {
      chip.appendChild(mark);
    } else {
      const clone = img.cloneNode(false);
      clone.removeAttribute('class');
      clone.setAttribute('alt', '');
      clone.setAttribute('loading', 'lazy');
      chip.appendChild(clone);
    }
    logos.appendChild(chip);
    logoCount += 1;
  }
  head.appendChild(logos);

  // termín plní etaRender() (jen doručované skupiny); prázdný span drží mřížku
  head.appendChild(node('span', 'bs-co-grp__eta'));

  const price = groupPriceLabel(
    wrappers.map(function (w) {
      return methodPrice(w);
    })
  );
  head.appendChild(
    node('span', 'bs-co-grp__price' + (price && price.free ? ' is-free' : ''), price ? price.text : '')
  );
  if (!single) head.appendChild(icon('chevron-down', 'bs-co-grp__chev'));
  card.appendChild(head);

  const list = node('div', 'bs-co-grp__list');
  list.id = 'bs-co-grp-' + def.key;
  head.setAttribute('aria-controls', list.id);
  for (const w of wrappers) {
    w.classList.add('bs-co-opt');
    list.appendChild(w);
  }
  card.appendChild(list);
  if (def.key === 'pickup') card.appendChild(buildPickupInfo());
  table.appendChild(card);
  return card;
}

// Logo dopravce u metody: nativní obrázek Shoptetu (101×50 px, GLS dokonce PNG)
// nahradíme vlastním vektorem ze spritu (zadání Tomáše 2026-09-05). Nativní
// `<img>` zůstává v DOM skrytý — je to obsah platformy a po AJAXu se vrací.
// Idempotentní: běží i ze `sync()`, kde default JS vyměňuje celé `.radio-wrapper`.
// Oficiální značka platby ze Shoptet Pay (`payment_logos/shoptetpay-*.svg`)
// jako klon nativního obrázku; jiný obrázek metody (generické `bank_*.png`,
// vlastní upload) značku nedává → null a medailonek dostane ikonu.
function paymentBrandImg(wrapper) {
  const img = wrapper.querySelector('img[src*="/payment_logos/shoptetpay-"]');
  if (!img) return null;
  const clone = document.createElement('img');
  clone.src = img.getAttribute('src');
  clone.alt = '';
  clone.loading = 'lazy';
  return clone;
}

function swapMethodLogo(wrapper) {
  const box = wrapper.querySelector('.payment-logo');
  if (!box || box.querySelector('.bs-logo')) return;
  const mark = carrierLogo(shipMethodName(wrapper));
  if (!mark) return;
  box.classList.add('bs-co-opt__logo--vector');
  box.insertBefore(mark, box.firstChild);
}

// Nativní nápověda metody („?") sedí jako sourozenec názvu → k názvu.
function inlineTooltip(wrapper) {
  const tip = wrapper.querySelector('.question-tooltip');
  const name = wrapper.querySelector('.shipping-billing-name');
  if (!tip || !name || tip.parentNode === name) return;
  const holder = tip.parentNode;
  name.appendChild(tip);
  if (holder && holder !== name && !holder.childNodes.length) holder.remove();
}

// Výběr výdejního místa (handover: podtržený červený odkaz „Zvolte odběrné
// místo" → po výběru text místa + odkaz „Změnit"). Nativní widget přepisuje
// obsah TÉHOŽ `.sublabel.new-widget-choose` na `Název místa <a class="chosen">Změnit</a>`
// (ověřeno proklikem 2026-08-20); stav „vybráno" se pozná podle `a.chosen`.
function pickupDecorate(choose) {
  const a = choose.querySelector('a');
  if (!a) return;
  const set = a.classList.contains('chosen');
  choose.classList.toggle('bs-co-opt__pick--set', set);
  if (!set) {
    const val = choose.querySelector('.bs-co-pt__val');
    if (val) val.remove();
    return;
  }
  // název místa leží v holém textovém uzlu vedle odkazu — obalit, ať jde stylovat
  const label = [].slice
    .call(choose.childNodes)
    .filter(function (n) {
      return n.nodeType === 3;
    })
    .map(function (n) {
      return n.textContent;
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  const stood = choose.querySelector('.bs-co-pt__val');
  if (stood && !label) return; // beze změny
  if (stood) stood.remove();
  for (const n of [].slice.call(choose.childNodes)) {
    if (n.nodeType === 3) n.remove();
  }
  choose.insertBefore(node('span', 'bs-co-pt__val', label), choose.firstChild);
}

function initPickupPoint(wrapper) {
  const choose = wrapper.querySelector('.sublabel.new-widget-choose');
  if (!choose || choose.dataset.bsCo) return;
  choose.dataset.bsCo = '1';
  choose.classList.add('bs-co-opt__pick');
  pickupDecorate(choose);
  // widget zapisuje přepsáním obsahu — bez observeru by dekorace proběhla jen
  // při prvním renderu
  const obs = new MutationObserver(function () {
    pickupDecorate(choose);
    // poznámka „bez místa nelze pokračovat" padá s výběrem místa
    if (choose.querySelector('a.chosen')) clearPickNotes(wrapper);
    obs.takeRecords(); // vlastní mutace zpátky do observeru nepouštíme
  });
  obs.observe(choose, { childList: true, subtree: true, characterData: true });
}

// --- výdejní místo před odesláním ------------------------------------------
// Shoptet chybějící výdejní místo validuje až na serveru (POST Step1Validate →
// redirect zpět na krok s hláškou „Odběrné místo … nebylo vybráno"). Když je
// zvolená metoda s widgetem a místo chybí, klik na CTA zachytíme v capture fázi
// (tlačítko stojí MIMO form — `form="order-form"` — proto dokument, ne form),
// formulář neodejde a otevře se rovnou widget dopravce. Po výběru místa
// (nativní setPickupPoint zapíše branch, přepíše odkaz na „Změnit" a přepočítá)
// pokračujeme sami — zákazník na „Pokračovat" už klikl. Zavře-li widget bez
// výběru, zůstává na kroku s krátkou poznámkou u metody. Serverová hláška
// (vypršelá session, JS nestihl) widget otevře hned po načtení. (Rozhodnutí
// Tomáše 2026-08-21.)
function activeShippingWrapper() {
  const input = document.querySelector('#order-shipping-methods input[name="shippingId"]:checked');
  if (input) return input.closest('.radio-wrapper');
  return document.querySelector('#order-shipping-methods .radio-wrapper.active');
}

// wrapper metody podle hodnoty radia — default JS po AJAXu (změna metody,
// výběr místa) vyměňuje celé `.radio-wrapper` uzly, držená reference by zestárla
function shippingWrapperById(id) {
  if (!id) return null;
  const input = document.querySelector('#order-shipping-methods input[name="shippingId"][value="' + id + '"]');
  return input ? input.closest('.radio-wrapper') : null;
}

function shippingIdOf(wrapper) {
  const input = wrapper && wrapper.querySelector('input[name="shippingId"]');
  return input ? input.value : null;
}

// odkaz „Zvolte odběrné místo" metody s widgetem, když místo chybí (jinak null)
function missingPickupLink(wrapper) {
  if (!wrapper) return null;
  const link = wrapper.querySelector('.new-widget-choose a, .new-gen-choose a, .pickup-point-choose a');
  if (!link || link.classList.contains('chosen')) return null;
  const branch = wrapper.querySelector('input[name*="shippingMethodBranch"]');
  if (branch && branch.value) return null;
  return link;
}

// nová generace widgetu = <dialog> s iframem /action/pickupPointWidget/; starší
// metody otevírají colorbox
function pickupWidgetVisible() {
  const dialogs = document.querySelectorAll('dialog.new-logistics-widget');
  for (const d of dialogs) {
    if (d.hasAttribute('open') && d.offsetWidth > 0) return true;
  }
  const frames = document.querySelectorAll('iframe[src*="pickupPoint"]');
  for (const f of frames) {
    if (f.offsetWidth > 0) return true;
  }
  const cb = document.getElementById('colorbox');
  return !!cb && cb.offsetWidth > 0 && getComputedStyle(cb).display !== 'none';
}

function showPickNote(wrapper) {
  if (!wrapper || wrapper.querySelector('.bs-co-opt__note')) return;
  const info = wrapper.querySelector('.payment-info');
  if (info) info.appendChild(node('span', 'bs-co-opt__note', PICK_NOTE));
}

function clearPickNotes(root) {
  for (const n of (root || document).querySelectorAll('.bs-co-opt__note')) n.remove();
}

function initPickupGuard() {
  const form = document.getElementById('order-form');
  const table = document.getElementById('order-shipping-methods');
  if (!form || !table || form.dataset.bsCoPick) return;
  form.dataset.bsCoPick = '1';

  let pending = false; // widget otevřený z CTA → po výběru pokračovat
  let ticker = null;
  const settle = function () {
    if (ticker) clearInterval(ticker);
    ticker = null;
    pending = false;
  };

  // hlídka po otevření: výběr místa → pokračovat; zavření bez výběru → poznámka.
  // Metoda se drží podle ID (wrapper se dohledává živě — viz shippingWrapperById).
  const watch = function (methodId) {
    if (ticker) clearInterval(ticker);
    let seen = false;
    let ticks = 0;
    let closed = 0; // tiky po sobě se zavřeným widgetem a bez místa
    ticker = setInterval(function () {
      ticks += 1;
      if (!pending) {
        settle();
        return;
      }
      if (pickupWidgetVisible()) {
        seen = true;
        closed = 0;
        return;
      }
      const wrapper = shippingWrapperById(methodId);
      // zákazník mezitím přepnul metodu → nic nevnucovat
      if (!wrapper || shippingIdOf(activeShippingWrapper()) !== methodId) {
        settle();
        return;
      }
      if (!missingPickupLink(wrapper)) {
        settle();
        clearPickNotes();
        // nativní přepočet po výběru (mousedown → AJAX) nechat dojet
        setTimeout(function () {
          const cta = findNativeCta();
          if (cta) cta.click();
        }, 600);
        return;
      }
      // widget zavřený bez výběru (nebo se do 12 s vůbec neotevřel). Po „Vybrat"
      // zavře widget dialog DŘÍV, než přes postMessage doběhne setPickupPoint
      // (místo se zapíše o desítky ms později, na pomalém stroji víc) — tik
      // v té mezeře by skončil poznámkou, kterou výběr hned smaže, a pokračování
      // by nevystřelilo (#409, doloženo záznamem tiků). Proto „bez výběru" až
      // na druhém tiku po sobě.
      closed += 1;
      if ((seen && closed >= 2) || ticks > 30) {
        settle();
        showPickNote(wrapper);
      }
    }, 400);
  };

  const open = function (wrapper, link) {
    pending = true;
    clearPickNotes();
    const methodId = shippingIdOf(wrapper);
    // nativní mousedown na metodě s widgetem ho otevírá sám — už otevřený widget
    // neotvírat znovu (Shoptet by vyměnil iframe uprostřed načítání)
    if (!pickupWidgetVisible()) link.click(); // nativní handler: fetch widgetu + dialog
    watch(methodId);
  };

  const guard = function (event) {
    const wrapper = activeShippingWrapper();
    const link = missingPickupLink(wrapper);
    if (!link) return; // místo je, nebo metoda widget nemá → nativní odeslání
    event.preventDefault();
    event.stopImmediatePropagation();
    open(wrapper, link);
  };

  // CTA (i klik z mobilní lišty = live.click()) a Enter ve formuláři (implicitní
  // odeslání = syntetický klik na submit tlačítko)
  document.addEventListener(
    'click',
    function (event) {
      const btn = event.target && event.target.closest ? event.target.closest('button, input[type="submit"]') : null;
      if (!btn || btn.form !== form || (btn.type && btn.type !== 'submit')) return;
      guard(event);
    },
    true
  );
  form.addEventListener('submit', guard, true);

  // serverová hláška → widget hned (zákazník už pokračovat chtěl)
  const msg = document.querySelector('.messages .msg-error');
  if (msg && L.pickupMissingRe.test(msg.textContent || '')) {
    const wrapper = activeShippingWrapper();
    const link = missingPickupLink(wrapper);
    if (link) {
      setTimeout(function () {
        open(wrapper, link);
      }, 700);
    }
  }
}

// --- termín doručení --------------------------------------------------------
// Zdroj je VÝHRADNĚ nativní datum (`div.delivery-time`, jen košík a detail) —
// reálná expedice z `/admin/dostupnosti/`. Krok 2 nativní uzel nemá → termín
// se nese v sessionStorage; nedojede-li, tichá degradace.
const ETA_KEY = 'bsCoEta';
const ETA_TTL = 6 * 60 * 60 * 1000;
const ETA_DAYS = L.etaDays;

function etaToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function etaParseDate(text) {
  const m = /(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/.exec(text || '');
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  d.setHours(0, 0, 0, 0);
  return Number.isFinite(d.getTime()) ? d : null;
}

// `{ value, soon }` — JEDEN tvar pro všechny termíny kroku 2: „dnes 21.8." /
// „v sobotu 22.8." / „v úterý 8.9." (datum bez mezer). `soon` = do dvou dnů
// (zeleně), dál šedě. ⚠️ Vědomá odchylka od prototypu (rozhodnutí Tomáše
// 2026-09-05): prototyp psal vzdálený termín zkratkou „Po 24.8. u vás", takže
// vedle sebe stály tři tvary („v pondělí 7.9." PPL, „Út 8.9. u vás" GLS,
// „v neděli 6.9." odběr) — sjednoceno na tvar se dnem, rozdíl nese jen barva.
// Termín vyzvednutí na prodejně jde stejným tvarem.
function etaValue(date) {
  const diff = Math.round((date - etaToday()) / 86400000);
  const dm = date.getDate() + '.' + (date.getMonth() + 1) + '.';
  if (diff === 0) return { value: L.etaToday(dm), soon: true };
  return { value: ETA_DAYS[date.getDay()] + ' ' + dm, soon: diff > 0 && diff <= 2 };
}

// Termín rychlého dopravce vždy zeleně: je to ta informace navíc vedle běžného
// termínu sourozenců, i když leží dál než dva dny (PPL pondělí v pátek večer).
function etaFastValue(date) {
  const v = etaValue(date);
  v.soon = true;
  return v;
}

function etaStore(date) {
  try {
    sessionStorage.setItem(ETA_KEY, JSON.stringify({ d: date.getTime(), at: Date.now() }));
  } catch (e) {
    /* privátní režim — termín do kroku 2 nedojede */
  }
}

function etaLoad() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(ETA_KEY);
  } catch (e) {
    return null;
  }
  if (!raw) return null;
  let data = null;
  try {
    data = JSON.parse(raw);
  } catch (e) {
    return null;
  }
  if (!data || !data.d || !data.at || Date.now() - data.at > ETA_TTL) return null;
  const date = new Date(data.d);
  date.setHours(0, 0, 0, 0);
  return Number.isFinite(date.getTime()) ? { date: date } : null;
}

// Košík: zelený pás v kartě. Znění zůstává NATIVNÍ („Doručení možné do: 24.8.2026")
// — prototyp košíku ukazuje přesně tento formát (bez cut-off věty); den
// v týdnu se používá až v kroku 2.
function initCartEta() {
  const box = document.querySelector('.delivery-time');
  if (!box) return;
  box.classList.add('bs-co-eta');
  // ikona kalendáře u termínu doručení (zadání Tomáše 2026-09-04)
  if (!box.querySelector('.bs-icon')) box.insertBefore(icon('calendar'), box.firstChild);

  const strong = box.querySelector('strong');
  if (!strong) return;
  // Shoptet počítá s uzávěrkou 15:00 i v pátek (naše 14:30): datum, které osa
  // doručení přebíjí, se přepíše v témže tvaru (text uzlu, tooltip zůstává)
  const fixed = nativeDeliveryText(strong.textContent);
  if (fixed !== strong.textContent) strong.textContent = fixed;
  const date = etaParseDate(strong.textContent);
  if (!date) return;
  etaStore(date);
  if (box.dataset.bsCo) return;
  box.dataset.bsCo = '1';

  // `<strong>` NEVYMĚŇUJEME (nese nativní tooltip); holý text „Doručení možné
  // do:" jen obalíme, aby šel stylovat
  strong.classList.add('bs-co-eta__val');
  for (const n of [].slice.call(box.childNodes)) {
    if (n.nodeType === 3 && n.textContent.trim()) {
      const lead = node('span', 'bs-co-eta__lead', n.textContent.trim());
      box.replaceChild(lead, n);
    }
  }
  // nativní termín (ikona · popisek · datum) do vlastního obalu — stejný tvar
  // jako řádek vyzvednutí, na mobilu (v3 §2) jde každý termín na vlastní řádek
  const deliver = node('span', 'bs-co-eta__deliver');
  for (const n of [].slice.call(box.childNodes)) {
    if (n.nodeType === 3 && !n.textContent.trim()) continue;
    deliver.appendChild(n);
  }
  box.appendChild(deliver);

  // Vedle termínu doručení i nejbližší termín vyzvednutí na prodejně (zadání
  // Tomáše 2026-08-31). Nativní pás mluví jen o doručení, přitom osobní odběr
  // je v nabídce vždycky — i u nadrozměrného košíku, kde doručovací metody
  // mizí. Datum drží tvar nativního řádku (bez mezer, s rokem).
  const pickup = nextPickupDay(new Date());
  const row = node('span', 'bs-co-eta__pickup');
  row.appendChild(icon('store'));
  row.appendChild(node('span', 'bs-co-eta__lead', CART_PICKUP_LEAD));
  row.appendChild(node('span', 'bs-co-eta__val', etaNativeFormat(pickup)));
  box.appendChild(row);

  // Zvláštní závoz dopravce (deliveryTimeline.js `t.fast`): v okně (GLS sobota
  // čt 15:00 → pá 14:30, PPL neděle pá 14:30 → so 24:00) nahradí dřívější termín
  // nativní přímo v řádku „Doručení možné do" (review Tomáše 2026-09-04 — tři
  // data v pásu byla nesmysl; dřívější třetí řádek „S GLS už v sobotu" zanikl).
  // Slib jen s dopravcem v nabídce TOHOTO KOŠÍKU: nabídka se čte ze stránky
  // možností dopravy první položky S cookies (Shoptet ji počítá pro košík
  // + produkt), nadrozměrný košík GLS/PPL nemá a termín nedostane; bez
  // sítě/dat zůstává nativní termín (bezpečný stav). Nativní `<strong>` zůstává
  // v DOM (tooltip, zdroj data pro krok 2), v rychlém stavu ho jen schová CSS
  // (`.bs-co-eta--fast`).
  const t = deliveryTimeline(new Date(), date);
  const href = t.fast ? cartShippingHref() : null;
  if (!href) return;
  loadShippingOptions(href, { withCart: true, cartKey: cartFingerprint() }).then(function (data) {
    const rows = (data && data.rows) || [];
    const names = rows.map(function (r) {
      return r.name;
    });
    if (!fastOffered(t, names) || !box.isConnected || box.querySelector('.bs-co-eta__val--fast')) return;
    deliver.appendChild(node('span', 'bs-co-eta__val bs-co-eta__val--fast', etaNativeFormat(t.fast.deliverDate)));
    // štítek nese LOGO dopravce (zadání Tomáše 2026-09-05), text jde do sr-only
    deliver.appendChild(carrierTag(node('span', 'bs-co-eta__tag'), t.fast.carrier, t.fast.tag));
    box.classList.add('bs-co-eta--fast');
  });
}

// Stránka možností dopravy pro KOŠÍK: Shoptet ji má jen per produkt
// (`/<slug>:moznosti-dopravy/`, viz shippingModel.js), s cookies ji ale počítá
// pro košík + produkt — u položky, která v košíku už je, je to nabídka košíku.
// Bere se první položka košíku.
function cartShippingHref() {
  const a = document.querySelector('.cart-table .p-name a[href]');
  if (!a) return null;
  let path = '';
  try {
    path = new URL(a.getAttribute('href'), location.href).pathname;
  } catch (e) {
    return null;
  }
  if (!/^\/[^:]+\/$/.test(path)) return null;
  return path.replace(/\/$/, '') + page('shippingOptions');
}

// „5.9.2026" — tvar nativního řádku košíku (bez mezer, s rokem)
function etaNativeFormat(d) {
  return d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear();
}

// Krok 2: termín do buňky hlavičky doručovaných skupin i do sloupce termínu
// každé podvolby (prototyp má termín na obou úrovních; Shoptet zná jeden termín
// pro celý košík, takže podvolby nesou tentýž — s výjimkou zvláštního závozu níž).
// Osobní odběr nativní datum NEBERE (je to termín doručení kurýrem, ne termín
// připravení na prodejně) — nese nejbližší otevírací den prodejny, stejným
// tvarem a stejně zeleně (zadání Tomáše 2026-08-31).
//
// Zvláštní závoz dopravce (deliveryTimeline.js `t.fast`; GLS sobota — zadání
// Tomáše 2026-09-02, PPL neděle — 2026-09-05): v okně dostanou podvolby
// rychlého dopravce dřívější termín (sobotu / pondělí), ostatní drží nativní;
// hlavička skupiny nese nejbližší termín svých podvoleb (u skupin s podvýběrem
// je stejně skrytá). Nad skupinami se v okně ukáže věta `fastPromo` — jen když
// je dopravce v nabídce košíku (nadrozměr ji nedostane).
const ETA_GROUPS = ['address', 'box'];

function optEtaCell(wrapper) {
  let cell = wrapper.querySelector('.bs-co-opt__eta');
  if (cell) return cell;
  const label = wrapper.querySelector('label');
  if (!label) return null;
  cell = node('span', 'bs-co-opt__eta');
  const info = label.querySelector(':scope > .payment-info');
  if (info) label.insertBefore(cell, info.nextSibling);
  else label.appendChild(cell);
  return cell;
}

// Věta ke zvláštnímu závozu pod hlavičkou karty dopravy (řeč pásu nadrozměru
// `.bs-co-ship__note`). Existuje JEN v okně a jen s dopravcem mezi metodami; mimo
// okno se z DOM odstraní — skrytý placeholder by paritnímu harnessu rozbil
// měření sourozenců (rect 0×0). Idempotentní: vytváří jen chybějící, maže jen
// existující, text zapisuje jen při změně (etaRender běží i z MutationObserveru).
function syncFastNote(delivery, text) {
  if (!delivery) return;
  let note = delivery.querySelector('.bs-co-ship__note--fast');
  if (!text) {
    if (note) note.remove();
    return;
  }
  if (!note) {
    note = node('p', 'bs-co-ship__note bs-co-ship__note--fast');
    note.appendChild(icon('truck'));
    note.appendChild(node('span', 'bs-co-ship__note-text'));
    const head = delivery.querySelector('.bs-co-box__head');
    if (head) head.insertAdjacentElement('afterend', note);
    else delivery.insertBefore(note, delivery.firstChild);
  }
  setText(note.querySelector('.bs-co-ship__note-text'), text);
}

// Buňka termínu (hlavička skupiny i podvolba) = ikona kalendáře · text
// (zadání Tomáše 2026-09-04). Zápis jen při změně — etaRender() volá sync()
// z MutationObserveru a bezpodmínečný zápis by roztočil nekonečnou smyčku;
// struktura se staví jen jednou.
function etaCellWrite(cell, value) {
  let txt = cell.querySelector('.bs-co-eta-txt');
  if (!txt) {
    cell.textContent = '';
    cell.appendChild(icon('calendar'));
    txt = node('span', 'bs-co-eta-txt');
    cell.appendChild(txt);
  }
  setText(txt, value);
}

// Štítek „Nejrychlejší" v NÁZVU metody (rychlý dopravce vedle sourozenců s běžným termínem;
// review Tomáše 2026-09-04). Jde dovnitř `.shipping-billing-name` před „?" —
// jako sourozenec ve flexu by se na mobilu odlomil (stejně jako nápověda);
// `shipMethodName()` ho přeskakuje. Idempotentní (sync z MutationObserveru).
function nameTagWrite(wrapper, tag, promo) {
  const name = wrapper.querySelector('.shipping-billing-name');
  if (!name) return;
  let chip = name.querySelector('.bs-co-opt__tag');
  if (!tag) {
    if (chip) chip.remove();
    return;
  }
  if (!chip) {
    chip = node('span', 'bs-co-opt__tag');
    const tip = name.querySelector('.question-tooltip');
    if (tip) name.insertBefore(chip, tip);
    else name.appendChild(chip);
  }
  // přepnutí jen při změně — sync běží z MutationObserveru
  if (chip.classList.contains('bs-co-opt__tag--promo') !== !!promo) chip.classList.toggle('bs-co-opt__tag--promo', !!promo);
  setText(chip, tag);
}

// Jediný štítek v názvu metody (plán kampaní 6. 10.): akce na dopravu
// („Akce do 25. 10.", A1 #322) u metody, kterou nabídka účtuje za akční cenu
// (`isPromoRow` — rozhoduje cena od platformy, ne zrcadlo), jinak „Nejrychlejší".
// Akce má přednost: je to cena, závoz je jen termín.
function methodTag(group, wrapper, name, fastest, promo) {
  if (promo && isPromoRow({ group: group, name: name, price: methodPrice(wrapper) }, promo)) {
    return { text: L.promoTag(promo.untilLabel), promo: true };
  }
  return { text: fastest ? LABEL_FASTEST : '', promo: false };
}

// V okně zvláštního závozu rychlý dopravce PRVNÍ ve skupině (nejrychlejší volba
// nahoře — zadání Tomáše 2026-09-04). Řadí ŽIVÉ wrappery ze seznamu skupiny:
// default JS umí uzly vyměnit, takže zapamatované pole by drželo odpojené uzly
// a appendChild by je vrátil do DOM jako duplikáty. `c.inputs` se tím zároveň
// obnoví (klik na hlavičku skupiny vybírá první z nich → rychlý dopravce).
// Idempotentní: DOM hýbe jen když pořadí nesedí, jinak by MutationObserver
// točil smyčku.
function orderFastFirst(c, t) {
  const list = c.card.querySelector('.bs-co-grp__list');
  if (!list) return;
  const live = [].slice.call(list.children).filter(function (el) {
    return el.classList.contains('radio-wrapper');
  });
  const sorted = fastFirst(t, live, function (w) {
    return shipMethodName(w) || '';
  });
  c.inputs = sorted;
  const inOrder = live.every(function (w, i) {
    return w === sorted[i];
  });
  if (inOrder) return;
  sorted.forEach(function (w) {
    list.appendChild(w);
  });
}

function etaRender(cards, delivery) {
  // Termín vyzvednutí nezávisí na nativním datu (počítá se z otevírací doby),
  // proto se kreslí i tehdy, když termín doručení do kroku 2 nedojel.
  const pickup = etaValue(nextPickupDay(new Date()));
  for (const c of cards) {
    if (c.def.key !== 'pickup') continue;
    const cell = c.card.querySelector('.bs-co-grp__eta');
    if (!cell) continue;
    etaCellWrite(cell, pickup.value);
    c.card.classList.toggle('bs-co-grp--eta-soon', pickup.soon);
  }

  // štítky v názvech metod: akce na dopravu termín nepotřebuje, „Nejrychlejší"
  // jen s termínem z košíku (níž ho smyčka doplní do `fastest`)
  const fastest = new Set();
  const eta = etaLoad();
  if (!eta) {
    syncFastNote(delivery, '');
    writeMethodTags(cards, fastest);
    return;
  }
  const label = etaValue(eta.date);
  // dřívější termín rychlého dopravce jen v okně (t.fast), jinak všechny podvolby nativní
  const t = deliveryTimeline(new Date(), eta.date);
  const fast = t.fast ? etaFastValue(t.fast.deliverDate) : null;
  let anyFast = false;

  for (const c of cards) {
    if (ETA_GROUPS.indexOf(c.def.key) === -1) continue;
    if (fast) orderFastFirst(c, t);
    const names = c.inputs.map(function (w) {
      return shipMethodName(w) || '';
    });
    const groupFast = !!fast && fastOffered(t, names);
    // štítek „Nejrychlejší" jen když má skupina i metodu bez závozu — skupina
    // jen z jednoho dopravce nemá proti čemu být nejrychlejší
    const mixed =
      groupFast &&
      names.some(function (n) {
        return !fastCarrier(t, n);
      });
    c.inputs.forEach(function (w, i) {
      const isFast = groupFast && fastCarrier(t, names[i]);
      const sub = optEtaCell(w);
      if (!sub) return;
      const v = isFast ? fast : label;
      etaCellWrite(sub, v.value);
      if (isFast && mixed) fastest.add(w);
      sub.classList.toggle('is-soon', v.soon);
      sub.classList.toggle('is-fast', isFast);
    });
    anyFast = anyFast || groupFast;
    const cell = c.card.querySelector('.bs-co-grp__eta');
    if (!cell) continue;
    const head = groupFast ? fast : label;
    etaCellWrite(cell, head.value);
    c.card.classList.toggle('bs-co-grp--eta-soon', head.soon);
  }

  syncFastNote(delivery, anyFast ? fastPromo(t) : '');
  writeMethodTags(cards, fastest);
}

// Jediný štítek v názvu každé metody doručení na adresu a na výdejní místo
// (`methodTag`): akce na dopravu, jinak „Nejrychlejší" u metod z `fastest`.
// U metody za akční cenu navíc přeškrtnutá cena ceníku pod cenou (vzor modalu
// Možnosti doručení, rozhodnutí Tomáše 2026-10-06).
function writeMethodTags(cards, fastest) {
  const promo = shippingPromo();
  for (const c of cards) {
    if (ETA_GROUPS.indexOf(c.def.key) === -1) continue;
    c.inputs.forEach(function (w) {
      const tag = methodTag(c.def.key, w, shipMethodName(w) || '', fastest.has(w), promo);
      nameTagWrite(w, tag.text, tag.promo);
      wasPriceWrite(w, tag.promo ? priceText(promo.was) : '');
    });
  }
}

// Přeškrtnutá cena ceníku jako atribut cenovky (SCSS ji kreslí `::after` pod
// cenou): cenovka je buňka mřížky řádku a Shoptet její text přepisuje AJAXem,
// atribut přepis textu přežije. Zápis jen při změně — sync běží z observeru.
function wasPriceWrite(wrapper, was) {
  const price = wrapper.querySelector('.payment-shipping-price');
  if (!price) return;
  if (!was) {
    if (price.hasAttribute('data-bs-was')) price.removeAttribute('data-bs-was');
    return;
  }
  if (price.getAttribute('data-bs-was') !== was) price.setAttribute('data-bs-was', was);
}

function initShipping() {
  const table = document.getElementById('order-shipping-methods');
  const billing = document.getElementById('order-billing-methods');
  const delivery = document.querySelector('.co-delivery-method');
  const payment = document.querySelector('.co-payment-method');
  if (delivery) delivery.classList.add('bs-co-box', 'bs-co-box--ship');
  if (payment) payment.classList.add('bs-co-box', 'bs-co-box--pay');

  // šedý hlavičkový pás karty; texty dle handoveru (copy je finální),
  // u platby s ikonou zámku „Zabezpečené a šifrované"
  for (const box of [delivery, payment]) {
    if (!box) continue;
    const h = heading(box);
    if (!h || h.dataset.bsCo) continue;
    h.dataset.bsCo = '1';
    h.classList.add('bs-co-box__head');
    h.textContent = box === payment ? PAY_TITLE : SHIP_TITLE;
    if (box === payment) {
      const meta = node('span', 'bs-co-box__meta');
      meta.appendChild(icon('lock'));
      meta.appendChild(node('span', null, PAY_SECURE));
      h.appendChild(meta);
    } else {
      // štítek hmotnosti a pásma košíku (mock 2026-09-02, varianta B)
      renderWeightChip(h);
    }
  }

  if (table && !table.dataset.bsCo) {
    table.dataset.bsCo = '1';
    table.classList.add('bs-co-ship');

    // nadrozměrné metody u běžné nabídky rovnou pryč (viz hideRedundantOversize)
    const wrappers = hideRedundantOversize(table);
    const buckets = {};
    for (const w of wrappers) {
      inlineTooltip(w);
      swapMethodLogo(w);
      initPickupPoint(w);
      const key = classifyShipping(w);
      (buckets[key] = buckets[key] || []).push(w);
    }

    const cards = [];
    for (const def of SHIP_GROUPS) {
      const list = buckets[def.key];
      if (!list || !list.length) continue;
      cards.push({ def: def, card: buildShipGroup(def, list, table), inputs: list });
    }

    // zúžená nabídka = žádná výdejní místa a na adresu jen nadrozměrná přeprava
    const hasBox = !!(buckets.box || []).length;
    const address = buckets.address || [];
    const onlyOversize =
      !hasBox &&
      address.length > 0 &&
      address.every(function (w) {
        return OVERSIZE_RE.test(shipMethodName(w) || '');
      });

    // škála hmotnosti pod poslední skupinou (v3 §4.2) — jen s běžnou
    // doručovanou metodou v nabídce (bez výdejních míst se o dopravě zdarma
    // mlčí, u čistě atypické nabídky ceny škály neplatí)
    renderWeightScale(table, { hasBox: hasBox, hasDelivery: hasBox || (address.length > 0 && !onlyOversize) });
    if (onlyOversize && delivery && !delivery.querySelector('.bs-co-ship__note')) {
      const note = node('p', 'bs-co-ship__note', OVERSIZE_NOTE);
      note.insertBefore(icon('truck'), note.firstChild);
      const head = delivery.querySelector('.bs-co-box__head');
      if (head) head.insertAdjacentElement('afterend', note);
      else delivery.insertBefore(note, delivery.firstChild);
    }
    const sync = function () {
      // vyměněný nadrozměrný wrapper po AJAXu třídu nemá → znovu (idempotentní,
      // zápis jen bez třídy, jinak by observer točil smyčku)
      hideRedundantOversize(table);
      // termín znovu při každém překreslení — default JS umí vyměnit celé
      // `.radio-wrapper`; etaRender je idempotentní (vč. věty o závozu dopravce,
      // kterou v okně vytvoří a mimo něj odstraní)
      etaRender(cards, delivery);
      // stav prodejny (otevřeno/zavřeno) — zápis jen při změně, sync() běží
      // i z MutationObserveru
      const pick = table.querySelector('.bs-co-pickup__status');
      if (pick) renderPickupStatus(pick);
      // vyměněný wrapper přišel s nativním obrázkem loga → znovu (idempotentní)
      for (const w of table.querySelectorAll('.radio-wrapper')) swapMethodLogo(w);
      for (const c of cards) {
        const on = c.inputs.some(function (w) {
          const input = w.querySelector('input[type="radio"]');
          return input && input.checked;
        });
        c.card.classList.toggle('is-on', on);
        const head = c.card.querySelector('.bs-co-grp__head');
        if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');
      }
    };

    for (const c of cards) {
      c.card.querySelector('.bs-co-grp__head').addEventListener('click', function () {
        clearPickNotes();
        if (c.card.classList.contains('is-on')) return;
        // volbu provede NATIVNÍ wrapper (default JS spočítá ceny a kombinace);
        // `label.inactive` = kombinace zakázaná platbou
        const first = c.inputs.find(function (w) {
          const input = w.querySelector('input[type="radio"]');
          const label = w.querySelector('label');
          return input && !input.disabled && !(label && label.classList.contains('inactive'));
        });
        if (first) selectNativeMethod(first);
        sync();
      });
      for (const w of c.inputs) {
        const input = w.querySelector('input[type="radio"]');
        if (input) {
          input.addEventListener('change', function () {
            bridgeNativeChange(w);
            sync();
          });
        }
      }
    }

    // ceny a stavy kombinací přepisuje default JS až po AJAXu → hlídat DOM
    if (cards.length) {
      const observer = new MutationObserver(sync);
      observer.observe(table, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'checked'],
      });
      sync();
      // Skrytá nadrozměrná metoda nesmí zůstat zvolená (Shoptet si pamatuje
      // volbu z minulé návštěvy) — zákazník by neviděl, co platí. Přepnout na
      // první běžnou metodu nativní cestou (mousedown → přepočet).
      const orphan = table.querySelector('.radio-wrapper.' + OVERSIZE_HIDDEN + ' input[type="radio"]:checked');
      if (orphan) {
        const first = wrappers.find(function (w) {
          const input = w.querySelector('input[type="radio"]');
          return input && !input.disabled;
        });
        if (first) selectNativeMethod(first);
      }
    }

    initPickupGuard();
  }

  if (billing) initPayments(billing);
}

// Platby = akordeon: radio | název | cena; vybraná volba rozbalí šedý info pás
// s popisem z NATIVNÍHO tooltipu (`data-original-title` — jediný zdroj popisu,
// který platforma nese). Nedostupná kombinace: nativní `label.inactive` +
// `em.not-possible-info` (znění platformní), vizuál řeší CSS.
function decorateBilling(billing) {
  for (const w of billing.querySelectorAll('.radio-wrapper')) {
    if (w.dataset.bsCo) continue;
    w.dataset.bsCo = '1';
    w.classList.add('bs-co-paycard');
    inlineTooltip(w);

    // medailonek platby podle názvu metody (mock 2026-09-02: dobírka bankovka,
    // převod/QR banka, karta online karta, prodejna peněženka; vybraná červeně)
    const label = w.querySelector('label');
    if (label && !label.querySelector('.bs-co-paycard__ico')) {
      const ico = node('span', 'bs-co-paycard__ico');
      ico.setAttribute('aria-hidden', 'true');
      // Shoptet Pay nese oficiální značky (Visa + Mastercard, Google Pay, Apple
      // Pay) jako nativní obrázek metody `payment_logos/shoptetpay-*.svg` — ty
      // jdou do medailonku místo obecné ikony karty (zadání Tomáše, den D
      // 2026-09-11); generické `bank_*.png` Shoptetu zůstávají u našich ikon
      const brand = paymentBrandImg(w);
      if (brand) {
        ico.classList.add('bs-co-paycard__ico--brand');
        ico.appendChild(brand);
      } else {
        ico.appendChild(icon(paymentIcon(shipMethodName(w))));
      }
      label.insertBefore(ico, label.firstChild);
    }

    const tip = w.querySelector('.question-tooltip');
    // živě běžíme DŘÍV, než default JS přesune `title` → `data-original-title`
    // (fixtures nesou až stav po jeho initu) → číst obojí
    const html = tip && (tip.getAttribute('data-original-title') || tip.getAttribute('title'));
    if (html && !w.querySelector('.bs-co-paycard__info')) {
      const info = node('div', 'bs-co-paycard__info');
      info.innerHTML = html; // obsah je platformní (nastavuje admin Shoptetu)
      w.appendChild(info);
    }

    // most pro přepnutí bez `mousedown` (šipky) — bez něj se souhrn nepřepočítá
    const input = w.querySelector('input[type="radio"]');
    if (input && !input.dataset.bsCoBound) {
      input.dataset.bsCoBound = '1';
      input.addEventListener('change', function () {
        bridgeNativeChange(w);
      });
    }
  }
}

function initPayments(billing) {
  if (billing.dataset.bsCo) return;
  billing.dataset.bsCo = '1';
  billing.classList.add('bs-co-pay');
  decorateBilling(billing);

  // default JS umí wrappery plateb přepsat (změna dopravy) → dekoraci opakovat;
  // vlastní zásahy se observeru nevrací (disconnect během práce)
  const obs = new MutationObserver(function () {
    obs.disconnect();
    decorateBilling(billing);
    obs.observe(billing, { childList: true, subtree: true });
  });
  obs.observe(billing, { childList: true, subtree: true });
}

// --- krok 3: informace o vás ---------------------------------------------
function initInfo() {
  const content = document.getElementById('checkoutContent');
  if (!content || content.dataset.bsCo) return;
  content.dataset.bsCo = '1';

  // karty se šedým hlavičkovým pásem (bez ikonových medailonků — handover
  // §Vizuální systém sekcí)
  for (const sel of ['.co-contact-information', '.co-billing-address fieldset', '.stay-in-touch']) {
    const box = content.querySelector(sel);
    if (!box) continue;
    box.classList.add('bs-co-card');
    const h = heading(box);
    if (!h || h.dataset.bsCo) continue;
    h.dataset.bsCo = '1';
    h.classList.add('bs-co-card__head');
  }

  const contact = content.querySelector('.co-contact-information');
  const billingBox = content.querySelector('.co-billing-address fieldset');
  const stay = content.querySelector('.stay-in-touch');

  // Kontaktní údaje: nadpis dle handoveru + „Povinná pole *" jako meta v pásu
  if (contact) {
    const h = heading(contact);
    if (h && !h.querySelector('.form-legend')) {
      setText(h.childNodes[0] && h.childNodes[0].nodeType === 3 ? h.childNodes[0] : h, CONTACT_TITLE);
      const legend = contact.querySelector('p.form-legend');
      if (legend) {
        legend.classList.add('bs-co-card__meta');
        h.appendChild(legend);
      }
    }
    // 2 sloupce: jméno plná šířka, e-mail | telefon
    contact.classList.add('bs-co-card--2col');
    for (const sel of ['input[name="email"]', '.phone-form-group']) {
      const el = contact.querySelector(sel);
      const group = el && (el.classList.contains('form-group') ? el : el.closest('.form-group'));
      if (group) group.classList.add('bs-co-field--half');
    }
  }

  // Fakturační adresa: switch „Nakupuji na firmu" do hlavičkového pásu
  if (billingBox) {
    const h = heading(billingBox);
    const company = content.querySelector('.form-group.company-shopping');
    if (h && company && company.parentNode !== h) {
      company.classList.add('bs-co-switch');
      h.appendChild(company);
    }
    const companyInfo = document.getElementById('company-info');
    if (companyInfo) companyInfo.classList.add('bs-co-sub', 'bs-co-sub--company');

    // „Doručit na jinou adresu" a „Zadat poznámku" patří dle handoveru pod
    // dělítko DO karty fakturační adresy — nativně leží mezi fieldsety,
    // přesun zůstává uvnitř form#order-form (ověřeno na fixture)
    const tail = [];
    for (const id of ['another-shipping', 'add-note']) {
      const input = document.getElementById(id);
      const group = input && input.closest('.form-group');
      if (!group) continue;
      group.classList.add('bs-co-check');
      tail.push(group);
      const reveal =
        id === 'another-shipping'
          ? document.getElementById('shipping-address')
          : document.getElementById('note');
      if (reveal) {
        reveal.classList.add('bs-co-sub');
        tail.push(reveal);
      }
    }
    if (tail.length) {
      const zone = node('div', 'bs-co-card__tail');
      billingBox.appendChild(zone);
      for (const el of tail) zone.appendChild(el);
    }
  }

  // přihlašovací karta: nativní `.helper` + checkbox registrace (přesun ze
  // „Zůstaňme v kontaktu" — obojí uvnitř form#order-form)
  const helper = content.querySelector('.cart-content > .helper');
  if (helper) {
    helper.classList.add('bs-co-login-hint');
    const reg = document.getElementById('set-registration');
    const regGroup = reg && reg.closest('.form-group');
    if (regGroup) {
      regGroup.classList.add('bs-co-check', 'bs-co-check--reg');
      helper.appendChild(regGroup);
    }
  }

  // „Zůstaňme v kontaktu": dotazníky Zboží.cz a Ověřeno se stěhují ze sidebaru
  // (handover je má v této kartě; zůstávají uvnitř form#order-form)
  if (stay) {
    removeNewsletterOptOut(stay);
    for (const group of document.querySelectorAll('#checkoutSidebar .form-group.consents')) {
      if (group.querySelector('input[type="checkbox"]')) stay.appendChild(group);
    }
    for (const group of stay.querySelectorAll('.form-group')) group.classList.add('bs-co-check');
    // shop bez dotazníků by tu nechal prázdnou kartu s nadpisem
    if (!stay.querySelector('.form-group input[type="checkbox"]')) stay.hidden = true;
  }

  // nápověda k DIČ visí ZA polem (osamělý „?") → k labelu
  const tipGroup = content.querySelector('.form-group-hasTooltip');
  if (tipGroup) {
    const tip = tipGroup.querySelector('.form-group-tooltip');
    const lab = tipGroup.querySelector('label');
    if (tip && lab && tip.parentNode !== lab) lab.appendChild(tip);
  }

  // předvolba telefonu: box „+420" + chevron, nativní select průhledně nad ním
  initPhoneCode(content);

  // adresní řádky dle prototypu: Město | (PSČ 130 + Země); u doručovací adresy
  // Město | PSČ. Zemi na dev určuje doprava z kroku 2 (Shoptet select skryje
  // a vypíše text) — kreslí se jako pole jen na čtení.
  initAddressRows(content, 'bill');
  initAddressRows(content, 'delivery');

  // poznámka: prototyp má textareu jen s placeholderem (label skrývá CSS)
  const remark = document.getElementById('remark');
  if (remark && !remark.getAttribute('placeholder')) remark.setAttribute('placeholder', NOTE_PLACEHOLDER);

  // souhlas s podmínkami (hidden consent + věta) zůstává v sidebaru nad CTA
  const consents = document.querySelectorAll('#checkoutSidebar .form-group.consents');
  for (const c of consents) c.classList.add('bs-co-fine');
}

// Newsletter v pokladně (zadání správce Ecomailu 2026-09-02, Notion „Kde je
// v e-mailingu Jiposu nevyužitý potenciál", nález z 12. 8.; rozhodl Tomáš):
// nativní políčko „Nepřeji si odebírat newslettery" NEFUNGUJE — napojení
// Shoptet → Ecomail zakládá kontakt každé objednávky jako přihlášený bez
// ohledu na volbu (ověřeno dvěma testovacími objednávkami). Zákazník tak
// dostával volbu, kterou systém ignoroval. Políčko jde z košíku pryč: obchodní
// sdělení stávajícím zákazníkům kryje výjimka pro zákazníky (z. 480/2004 Sb.),
// popis v obchodních podmínkách je věc klienta. Odstraňuje se JEN opt-out
// varianta (`doNotSendNewsletter`) — nepřítomné pole = Shoptet zákazníka
// přihlásí. Kdyby admin přepnul pokladnu na opt-in („Chci odebírat", jiné
// jméno pole), zůstává políčko viditelné — skrytý opt-in by nepřihlásil nikoho.
function removeNewsletterOptOut(stay) {
  const input = stay.querySelector('input[name="doNotSendNewsletter"]');
  const group = input && input.closest('.form-group');
  if (group) group.remove();
}

// Předvolba telefonu. Nativní `select.js-phone-code` nese „Česká republika +420"
// (216 zemí, vlajkový dropdown skrývá _forms) — prototyp chce box 96 px jen
// s předvolbou. Select zůstává ve formu (name=phoneCode) a funkční (průhledný
// nad boxem, klik otevře nativní seznam), viditelný je jen kód vybrané země.
function initPhoneCode(content) {
  const select = content.querySelector('select.js-phone-code');
  if (!select || select.closest('.bs-co-phonecode')) return;
  const wrap = node('span', 'bs-co-phonecode');
  const val = node('span', 'bs-co-phonecode__val');
  select.parentNode.insertBefore(wrap, select);
  wrap.appendChild(val);
  wrap.appendChild(icon('chevron-down'));
  wrap.appendChild(select);
  const sync = function () {
    const opt = select.options[select.selectedIndex];
    let code = '';
    if (opt) {
      try {
        code = (JSON.parse(opt.value) || {}).phoneCode || '';
      } catch (e) {
        code = '';
      }
      if (!code) {
        const m = /\+\d+/.exec(opt.textContent || '');
        code = m ? m[0] : '';
      }
    }
    setText(val, code);
  };
  sync();
  select.addEventListener('change', sync);
}

// Město + PSČ (+ Země) vedle sebe — obal `.bs-co-addr-row` (2 sloupce), PSČ a Země
// v páru `.bs-co-addr-pair` (130 px + zbytek). Vše zůstává uvnitř form#order-form.
function initAddressRows(content, prefix) {
  const city = content.querySelector('input[name="' + prefix + 'City"]');
  const zip = content.querySelector('input[name="' + prefix + 'Zip"]');
  const cityG = city && city.closest('.form-group');
  const zipG = zip && zip.closest('.form-group');
  if (!cityG || !zipG || cityG.closest('.bs-co-addr-row')) return;
  const countrySel = content.querySelector('#' + prefix + 'CountryId');
  const countryG = countrySel && countrySel.closest('.form-group');

  const row = node('div', 'bs-co-addr-row');
  cityG.parentNode.insertBefore(row, cityG);
  row.appendChild(cityG);
  if (countryG) {
    const pair = node('div', 'bs-co-addr-pair');
    row.appendChild(pair);
    pair.appendChild(zipG);
    pair.appendChild(countryG);
    // země jen na čtení: nativní `p.label.js-location` + text vedle skrytého selectu
    const lbl = countryG.querySelector('p.label.js-location');
    if (lbl) lbl.classList.add('bs-co-country-label');
    const box = countryG.querySelector('.form-control-wrapper.js-location:not(.js-location--hidden)');
    if (box) box.classList.add('bs-co-country');
  } else {
    row.appendChild(zipG);
  }
}

// --- děkovací stránka -------------------------------------------------------
// Centrovaná karta: zelený kruh, nadpis, věta s číslem objednávky, CTA.
// Nativní stránka pod ní nese rekapitulaci VČETNĚ údajů k převodu (účet, VS,
// QR) — jediné místo, kde je zákazník uvidí, takže se nekrátí, jen třídí.
const DONE_ICONS = [
  { sel: '.co-payment-method', icon: 'card' },
  { sel: '.co-order', icon: 'cart' },
];

function initDone() {
  const inner = document.querySelector('#content .content-inner');
  if (!inner || inner.dataset.bsCo) return;
  inner.dataset.bsCo = '1';
  inner.classList.add('bs-co-done');

  const title = inner.querySelector('h1.order-summary-heading');
  if (title) {
    const hero = node('div', 'bs-co-done__hero');
    const badge = node('span', 'bs-co-done__ic');
    badge.appendChild(icon('check'));
    title.insertAdjacentElement('beforebegin', hero);
    hero.appendChild(badge);
    title.classList.add('bs-co-done__title');
    hero.appendChild(title);

    const lead = node('p', 'bs-co-done__lead');
    let n = hero.nextSibling;
    while (n && !(n.nodeType === 1 && n.classList.contains('recapitulation-wrapper'))) {
      const next = n.nextSibling;
      if (n.nodeType === 3 || n.nodeType === 1) lead.appendChild(n);
      n = next;
    }
    if (lead.textContent.trim()) hero.appendChild(lead);
  }

  for (const box of inner.querySelectorAll('.recapitulation-wrapper')) {
    box.classList.add('bs-co-done__box');
  }
  const boxes = inner.querySelectorAll('.bs-co-done__box');
  if (boxes.length > 1) boxes[boxes.length - 1].classList.add('bs-co-done__box--plain');

  for (const def of DONE_ICONS) {
    const box = inner.querySelector(def.sel);
    if (!box) continue;
    box.classList.add('bs-co-donesec');
    const h = box.querySelector('h4');
    if (!h || h.dataset.bsCo) continue;
    h.dataset.bsCo = '1';
    h.classList.add('bs-co-donesec__head');
    const badge = node('span', 'bs-co-donesec__ic');
    badge.appendChild(icon(def.icon));
    h.insertBefore(badge, h.firstChild);
  }

  const pay = inner.querySelector('.recapitulation-table-payment');
  if (pay) {
    pay.classList.add('bs-co-donepay');
    pay.insertBefore(node('p', 'bs-co-donepay__lbl', L.donePayLabel), pay.firstChild);
  }

  const cta = inner.querySelector('.order-complete-links a');
  if (cta) {
    cta.classList.add('bs-co-done__cta');
    cta.appendChild(icon('arrow-right'));
  }
}

// --- rekapitulace (pravý panel) --------------------------------------------

// fotka 48 px s qty badge (černý kruh vpravo nahoře) — thumb je NÁŠ uzel,
// vkládá se před název položky nativního souhrnu
function sumThumb(src, qty) {
  const thumb = node('span', 'bs-co-sum__thumb');
  if (src) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    thumb.appendChild(img);
  }
  if (qty) thumb.appendChild(node('span', 'bs-co-sum__qty', String(qty)));
  return thumb;
}

function decorateSummaryBox(box) {
  box.classList.add('bs-co-sum');
  const h = heading(box);
  const items = box.querySelectorAll('.order-summary-item[data-micro="cartItem"]');
  if (h) {
    h.classList.add('bs-co-sum__head');
    let count = h.querySelector('.bs-co-sum__head-count');
    if (!count) {
      count = node('span', 'bs-co-sum__head-count');
      h.appendChild(count);
    }
    setText(count, itemsWord(items.length));
  }

  const thumbs = thumbsLoad();
  const dlInfo = shoptetData('cartInfo');
  const dlItems = dlInfo && Array.isArray(dlInfo.cartItems) ? dlInfo.cartItems : [];
  for (const item of items) {
    if (item.dataset.bsCo) continue;
    item.dataset.bsCo = '1';
    const amount = item.querySelector('.cart-item-amount');
    const qty = amount ? parseInt(amount.textContent, 10) : NaN;
    const guid = item.getAttribute('data-micro-identifier');
    item.insertBefore(sumThumb(guid && thumbs[guid], Number.isFinite(qty) ? qty : null), item.firstChild);

    // přeškrtnutá běžná cena × ks + procento slevy nad řádkovou cenou (v3
    // §7.1); párování na dataLayer přes guid, fallback název (vzor cartModal)
    const nameEl = item.querySelector('[data-testid="recapItemName"]');
    const wanted = nameEl ? nameEl.textContent.replace(/\s+/g, ' ').trim() : '';
    const dl = dlItems.find(function (it) {
      if (!it) return false;
      if (guid && it.guid === guid) return true;
      return !!wanted && String(it.name || '').replace(/\s+/g, ' ').trim() === wanted;
    });
    const priceCell = item.querySelector('.cart-item-price');
    const was = priceCell ? sumWasRow(dl, Number.isFinite(qty) ? qty : 1) : null;
    if (was) priceCell.appendChild(was); // pod cenou (review 2026-09-09)
  }

  // řádky metod v souhrnu kroku 3 (bez data-micro) — název vlevo, cena vpravo
  for (const item of box.querySelectorAll('.order-summary-item')) {
    if (item.getAttribute('data-micro') === 'cartItem') continue;
    if (item.classList.contains('helper') || item.classList.contains('price')) continue;
    item.classList.add('bs-co-sum__line');
  }

  // popisek řádku zboží dle prototypu (výhody souhrn od v3 nenese — bar
  // s milníky je jen v kartě košíku)
  const helper = box.querySelector('.order-summary-item.helper');
  const helperLabel = helper && helper.querySelector('strong');
  if (helperLabel && !helperLabel.querySelector('*')) setText(helperLabel, GOODS_LABEL);

  // úspora není řádkem rozpadu (v3 §7) — nese ji blok „Celkem k úhradě"
  const priceItem = box.querySelector('.order-summary-item.price');
  if (priceItem) priceItem.classList.add('bs-co-sum__totalbox');
  const priceWrap = box.querySelector('.price-wrapper');
  if (priceWrap) decorateTotal(priceWrap);
}

// Celkový blok: popisky bez nativních dvojteček (prototyp „Celkem k úhradě",
// „Cena bez DPH"); částky jsou nativní, jen se čtou. V3 §7.2–7.3: nad velkou
// částkou přeškrtnutý součet před slevou (zobrazená celková cena + úspora,
// tj. vč. dopravy a platby v krocích 2–3) a pod „Cena bez DPH" žlutý pásek
// „Ušetřili jste X Kč · −N %" — obojí jen při úspoře > 0. Sleva se v rozpadu
// ceny jako samostatný řádek nekreslí (vypadala by jako platba). Idempotentní:
// nativní `.price-wrapper` po AJAXu přichází nový (krok 1 celý košík, kroky
// 2–3 `#summary-box`), v témže uzlu se jen přepisují texty.
function decorateTotal(priceWrap) {
  priceWrap.classList.add('bs-co-sum__total');
  const primary = priceWrap.querySelector('.price-label.price-primary');
  if (primary) setText(primary, TOTAL_LABEL);
  const secondary = priceWrap.querySelector('.price-label.price-secondary');
  if (secondary) setText(secondary, (secondary.textContent || '').trim().replace(/:$/, ''));

  const amount = priceWrap.querySelector('.price.price-primary');
  const total = amount ? parsePrice(amount.textContent) : null;
  const sv = cartSavings();
  let was = priceWrap.querySelector('.bs-co-sum__total-was');
  let ribbon = priceWrap.querySelector(':scope > .bs-co-sum__ribbon');
  if (!sv || total == null) {
    if (was) was.remove();
    if (ribbon) ribbon.remove();
    return;
  }
  // sloupec (přeškrtnutý součet / částka) místo holé částky v mřížce bloku
  let col = priceWrap.querySelector(':scope > .bs-co-sum__total-col');
  if (!col) {
    col = node('span', 'bs-co-sum__total-col');
    amount.parentNode.insertBefore(col, amount);
    col.appendChild(amount);
  }
  if (!was) {
    was = node('s', 'bs-co-sum__total-was');
    col.appendChild(was); // pod částkou — částka drží rovinu s „Celkem k úhradě"
  }
  setText(was, priceText(total + sv.saved));
  if (!ribbon) {
    ribbon = node('div', 'bs-co-sum__ribbon');
    ribbon.appendChild(icon('tag'));
    ribbon.appendChild(node('span', 'bs-co-sum__ribbon-txt'));
    ribbon.appendChild(node('span', 'bs-co-sum__ribbon-pct'));
    priceWrap.appendChild(ribbon);
  }
  ribbon.classList.toggle('bs-co-sum__ribbon--vo', !!sv.wholesale);
  // po dobu kampaně s vlastní větou (Black Friday) nese úsporu její název;
  // velkoobchod má úsporu ceníku, ne kampaně
  const campaign = sv.wholesale ? null : activeCampaign();
  const retail = (campaign && campaign.t.cartSaved) || SAVED_LABEL;
  const label = !sv.wholesale ? retail : sv.wholesale.name ? SAVED_VO_LABEL + sv.wholesale.name + ' ' : SAVED_VO_GENERIC;
  setText(ribbon.querySelector('.bs-co-sum__ribbon-txt'), label + priceText(sv.saved));
  setText(ribbon.querySelector('.bs-co-sum__ribbon-pct'), '−' + sv.percent + NBSP + '%');
}

// popisky CTA a odkazů zpět dle handoveru (krok 3 má „Objednat / s povinností
// platby" už nativně)
function labelNav(step) {
  const forward = findNativeCta();
  if (forward && CTA_LABELS[step]) {
    setText(forward.querySelector('.order-button-text') || forward, CTA_LABELS[step]);
  }
  const back = document.querySelector('.next-step .next-step-back');
  if (back && BACK_LABELS[step]) {
    if (!back.dataset.bsCo) {
      back.dataset.bsCo = '1';
      back.textContent = BACK_LABELS[step];
      back.insertBefore(icon('arrow-left'), back.firstChild);
    }
  }
}

// krok 1: souhrn se staví z tabulky košíku do vlastního aside sloupce
function initCartSummary() {
  const inner = document.querySelector('.cart-inner');
  if (!inner || inner.classList.contains('cart-empty')) return;

  let aside = inner.querySelector(':scope > .bs-co-aside');
  if (!aside) {
    aside = node('div', 'bs-co-aside');
    inner.appendChild(aside);
  }
  let asideIn = aside.querySelector(':scope > .bs-co-aside__in');
  if (!asideIn) {
    asideIn = node('div', 'bs-co-aside__in');
    aside.appendChild(asideIn);
  }

  const rows = document.querySelectorAll('table.cart-table tr.removeable');
  const dlInfo = shoptetData('cartInfo');
  const dlItems = dlInfo && Array.isArray(dlInfo.cartItems) ? dlInfo.cartItems : [];

  // hlavička
  let head = asideIn.querySelector(':scope > .bs-co-sum__head');
  if (!head) {
    head = node('span', 'bs-co-sum__head', SUM_TITLE);
    head.appendChild(node('span', 'bs-co-sum__head-count'));
  }
  setText(head.querySelector('.bs-co-sum__head-count'), itemsWord(rows.length));
  asideIn.appendChild(head);

  // položky — kompaktní zrcadlo tabulky (fotka + qty badge, název, součet);
  // seznam se při každém průchodu staví znovu (AJAX mění množství i položky)
  const oldList = asideIn.querySelector(':scope > .bs-co-sum__items');
  if (oldList) oldList.remove();
  const list = node('div', 'bs-co-sum__items');
  for (const row of rows) {
    const item = node('div', 'bs-co-sum__item');
    const img = row.querySelector('td.cart-p-image img');
    const qtyInput = row.querySelector('td.p-quantity input.amount');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
    item.appendChild(sumThumb(img && img.src, Number.isFinite(qty) ? qty : null));
    // `.main-link` je u běžné položky <a>, u položky služby fvStudio <span> —
    // selektor jen na <a> nechával službu v rekapitulaci beze jména (2026-09-11)
    const name = row.querySelector('td.p-name .main-link');
    item.appendChild(node('span', 'bs-co-sum__name', name ? name.textContent.trim() : ''));
    // sloupec ceny (v3 §7.1 + review Tomáše 2026-09-09): řádková cena v rovině
    // s názvem, přeškrtnutá běžná cena × ks + „−N %" POD ní
    const col = node('span', 'bs-co-sum__col');
    const code = row.getAttribute('data-micro-sku');
    const dl = dlItems.find(function (it) {
      return it && it.code === code;
    });
    const total = row.querySelector('td.p-total .price-final');
    col.appendChild(node('span', 'bs-co-sum__price', total ? total.textContent.trim() : ''));
    const was = sumWasRow(dl, qty);
    if (was) col.appendChild(was);
    item.appendChild(col);
    list.appendChild(item);
  }
  asideIn.appendChild(list);

  // slevový kupón — nativní blok se stěhuje do souhrnu; pole je viditelné
  // rovnou (toggle skrývá CSS), texty dle handoveru
  const coupon = document.querySelector('.coupon-input');
  if (coupon) {
    coupon.classList.add('bs-co-coupon');
    const input = coupon.querySelector('input[name="discountCouponCode"]');
    if (input) input.setAttribute('placeholder', COUPON_PLACEHOLDER);
    const btn = coupon.querySelector('.discount-coupon button[type="submit"]');
    if (btn) setText(btn, COUPON_APPLY);
    asideIn.appendChild(coupon);
  }

  // výhody v souhrnu nejsou (v3: „v sidebaru žádný duplikát" — bar s milníky
  // je jen v kartě košíku)

  // „Zboží celkem" (zrcadlo nativní částky) + hint + celková cena — prototyp
  // kroku 1 má řádek zboží, poznámku o dopravě a velký „Celkem k úhradě"
  const priceWrap = inner.querySelector('.price-wrapper');
  let goods = asideIn.querySelector(':scope > .bs-co-sum__goods');
  if (!goods) {
    goods = node('div', 'bs-co-sum__goods');
    goods.appendChild(node('span', null, GOODS_LABEL));
    goods.appendChild(node('span'));
  }
  const primary = priceWrap && priceWrap.querySelector('.price.price-primary');
  setText(goods.lastChild, primary ? primary.textContent.trim() : '');
  asideIn.appendChild(goods);

  // úspora se v rozpadu ceny jako samostatný řádek nekreslí (v3 §7 — vypadala
  // by jako platba); nese ji přeškrtnutý součet a žlutý pásek v bloku
  // „Celkem k úhradě" (decorateTotal)

  // řádek dárku dle prototypu (§79) — nativní řádek košíku je skrytý, tohle je
  // jediné místo, kde zákazník v kroku 1 vidí, co k objednávce dostane
  const gift = giftName();
  let giftRow = asideIn.querySelector(':scope > .bs-co-sum__gift');
  if (gift) {
    if (!giftRow) {
      giftRow = node('div', 'bs-co-sum__gift');
      giftRow.appendChild(node('span', 'bs-co-sum__gift-name'));
      giftRow.appendChild(node('span', 'bs-co-sum__gift-free', L.free));
    }
    setText(giftRow.querySelector('.bs-co-sum__gift-name'), GIFT_LABEL + ': ' + gift);
    asideIn.appendChild(giftRow);
  } else if (giftRow) {
    giftRow.remove();
  }

  let hint = asideIn.querySelector(':scope > .bs-co-sum__hint');
  if (!hint) {
    hint = node('span', 'bs-co-sum__hint', HINT_NEXT);
    hint.insertBefore(icon('truck'), hint.firstChild);
  }
  asideIn.appendChild(hint);
  if (priceWrap) {
    decorateTotal(priceWrap);
    asideIn.appendChild(priceWrap);
  }

  // CTA (nativní odkaz na krok 2); „Zpět do obchodu" už odešel do karty košíku
  const next = inner.querySelector('.next-step--cart') || document.querySelector('.next-step--cart');
  if (next) asideIn.appendChild(next);
  labelNav('cart');
}

function initSummary(step) {
  if (step === 'cart') {
    initCartSummary();
    return;
  }

  const row = document.querySelector('.row.cart-row');
  if (row) row.classList.add('bs-co-grid');
  const sidebar = document.getElementById('checkoutSidebar');
  if (sidebar) {
    sidebar.classList.add('bs-co-aside');
    const asideIn = sidebar.querySelector(':scope > .cart-content');
    if (asideIn) asideIn.classList.add('bs-co-aside__in');
  }

  const box = document.getElementById('summary-box');
  if (box) decorateSummaryBox(box);
  labelNav(step);
}

// Živé zrcadlení celkové částky (default JS ji po AJAXu přepisuje na místě).
// Observer visí na stabilním `#content-wrapper` (AJAX umí vyměnit celý
// `.cart-inner`) a zdroj se hledá znovu. (Nález B2, 2026-08-20.)
// Otisk košíku pro cache nabídky dopravy (`loadShippingOptions` withCart).
function cartFingerprint() {
  const info = shoptetData('cartInfo');
  const items = info && Array.isArray(info.cartItems) ? info.cartItems : [];
  return items
    .map(function (i) {
      return (i.code || i.priceId || '') + ':' + (i.quantity || '');
    })
    .join('|');
}

function mirrorPrice(target) {
  const root = document.getElementById('content-wrapper') || document.body;
  const apply = function () {
    const source =
      document.querySelector('#summary-box [data-testid="recapFullPrice"]') ||
      document.querySelector('[data-testid="recapFullPrice"]');
    if (!source) return;
    const next = (source.textContent || '').trim();
    if (target.textContent !== next) target.textContent = next;
  };
  apply();
  const observer = new MutationObserver(function () {
    // lišta odstraněná (prázdný košík, removeStickyCta) = observer už nemá komu psát
    if (!target.isConnected) {
      observer.disconnect();
      return;
    }
    apply();
  });
  observer.observe(root, { childList: true, characterData: true, subtree: true });
  return observer;
}

// --- mobil: platby před pokračováním (2026-09-13, zadání Tomáše) ------------
// GA4 po cutoveru 11. 9.: na mobilu klesla volba online platby (36 → 17 %),
// desktop beze změny. Není to vada — fixní lišta s CTA je vidět pořád a sekce
// plateb začíná až pod ohybem, takže zákazník pokračuje, aniž volbu platby
// uvidí (stará šablona měla tlačítko až POD platbami). Gate: dokud k platbám
// nedoscrolloval ani na platbu nesáhl, první tap na CTA lišty stránku jen
// posune na sekci plateb (+ fokus na nadpis pro čtečky); další tap pokračuje.
// Předvybraná platba (dobírka = preference klienta), pořadí i vzhled beze
// změny, žádný nový vizuální prvek. Stav jen v paměti stránky. Jen lišta
// (`.bs-co-bar` = mobil) a jen krok `shipping` — desktopový submit se netkne;
// pickup guard zůstává downstream (2. tap → `live.click()` → guard jako dnes).
const GATE_ROW_PX = 56; // ≈ řádek první platby: „viděno" = nadpis + první metoda nad lištou

function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function chromeHeights() {
  const head = document.querySelector('.bs-co-head');
  const bar = document.querySelector('.bs-co-bar');
  return {
    head: head ? head.getBoundingClientRect().height : 0,
    bar: bar ? bar.getBoundingClientRect().height : 0,
  };
}

function createPaymentsGate() {
  const box = document.querySelector('.co-payment-method');
  const billing = document.getElementById('order-billing-methods');
  const title = box && heading(box);
  if (!box || !billing || !title) return null;

  let seen = false;
  const markSeen = function () {
    seen = true;
    if (io) io.disconnect();
    io = null;
  };

  // „viděno" = seznam plateb zasahuje do viewportu zmenšeného o sticky hlavičku
  // a lištu + jeden řádek metody (aspoň první platba je nad lištou celá).
  // Pozoruje se kontejner, ne nadpis: zákazník, který proscrolloval řádky
  // plateb s nadpisem schovaným za hlavičkou, platby viděl. IO vydá první
  // záznam hned po pozorování → velký telefon / málo doprav = viděno od startu.
  let io = null;
  if (typeof IntersectionObserver === 'function') {
    const h = chromeHeights();
    io = new IntersectionObserver(
      function (entries) {
        if (entries.some((e) => e.isIntersecting)) markSeen();
      },
      { rootMargin: -Math.round(h.head) + 'px 0px ' + -Math.round(h.bar + GATE_ROW_PX) + 'px 0px', threshold: 0 }
    );
    io.observe(billing);
  } else {
    seen = true; // bez IO gate nedělat — jistější je pustit dál
  }

  // jakákoli interakce s platbou = viděno (kontejner default JS nevyměňuje,
  // jen wrappery uvnitř → delegace na `#order-billing-methods`)
  for (const type of ['mousedown', 'change', 'focusin']) {
    billing.addEventListener(type, markSeen, { passive: true });
  }

  // posun na nadpis pod sticky hlavičku; offset se počítá až při tapu (AJAX
  // přepočet doprav mění výšku bloku nad platbami)
  const reveal = function () {
    const live = document.querySelector('.co-payment-method');
    const target = (live && heading(live)) || title;
    const goal = function () {
      return Math.max(0, window.pageYOffset + target.getBoundingClientRect().top - chromeHeights().head - 12);
    };
    try {
      window.scrollTo({ top: goal(), behavior: reducedMotion() ? 'auto' : 'smooth' });
    } catch (e) {
      window.scrollTo(0, goal());
    }
    // Cíl spočítaný při tapu může zestárnout (AJAX přepočet doprav mění výšku
    // bloku nad platbami) → pozice se ~3 s hlídá a dorovnává skokem; dotyk
    // nebo kolečko zákazníka hlídku ukončí (jeho gesto má přednost). Skok JEN
    // v klidu (pozice se mezi dvěma tiky nehnula): Chromium okamžitý scrollTo
    // běžící smooth animaci nepřeruší, jen jí posune cíl o týž rozdíl, takže
    // zbytek animace přestřelí (#409: animace zadrhnutá na 776 z 942, skok
    // na 942 skončil na 1108, nadpis zajel pod hlavičku; dřívější „±173 px
    // přerenderování bloku dopravy" byl tentýž jev, blok se po načtení nemění).
    let touched = false;
    const onTouch = function () {
      touched = true;
    };
    window.addEventListener('touchstart', onTouch, { passive: true, once: true });
    window.addEventListener('wheel', onTouch, { passive: true, once: true });
    let ticks = 0;
    let lastY = window.pageYOffset;
    const settle = setInterval(function () {
      ticks += 1;
      if (touched || !target.isConnected || ticks > 8) {
        clearInterval(settle);
        window.removeEventListener('touchstart', onTouch);
        window.removeEventListener('wheel', onTouch);
        return;
      }
      const y = window.pageYOffset;
      const moving = Math.abs(y - lastY) > 1;
      lastY = y;
      if (moving) return; // animace ještě běží → počkat na klid
      const want = goal();
      if (Math.abs(y - want) > 8) window.scrollTo(0, want);
    }, 400);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      /* starý WebKit bez options */
    }
    markSeen();
  };

  return {
    // true = tap obsloužen posunem, CTA nepokračuje
    intercept: function () {
      if (seen) return false;
      reveal();
      return true;
    },
  };
}

// Fixní CTA lišta na mobilu — jediný řádek: „Celkem k úhradě" + cena | CTA.
// Klonuje NATIVNÍ submit (klik jen pouští originál); hledá se ZNOVU při každém
// kliku, AJAX uzel vyměňuje (nález U5 #19).
function findNativeCta() {
  return (
    document.getElementById('submit-order') ||
    document.getElementById('orderFormButton') ||
    document.getElementById('continue-order-button')
  );
}

function initStickyCta(step) {
  if (document.querySelector('.bs-co-bar')) return;
  const nativeCta = findNativeCta();
  if (!nativeCta) return;

  const bar = node('div', 'bs-co-bar');

  // Zpět (rozhodnutí Tomáše 2026-08-24): v liště se dá jít i o krok zpátky —
  // méně výrazné kruhové tlačítko se šipkou před souhrnem. Cíl bere nativní
  // odkaz `.next-step-back` (krok 2 → košík, krok 3 → doprava); v košíku
  // (krok 1) nativní zpět nevede na předchozí krok, ale do obchodu → lišta
  // tam tlačítko nemá.
  const nativeBack = document.querySelector('.next-step .next-step-back');
  if (BACK_LABELS[step] && nativeBack && nativeBack.getAttribute('href')) {
    const back = node('a', 'bs-co-bar__back');
    back.href = nativeBack.getAttribute('href');
    back.setAttribute('aria-label', BACK_LABELS[step]);
    back.title = back.getAttribute('aria-label');
    back.appendChild(icon('arrow-left'));
    bar.appendChild(back);
  }

  const info = node('div', 'bs-co-bar__info');
  info.appendChild(node('span', 'bs-co-bar__label', TOTAL_LABEL));
  const amount = node('strong', 'bs-co-bar__price');
  info.appendChild(amount);
  bar.appendChild(info);

  const btn = node('button', 'bs-co-bar__cta');
  btn.type = 'button';
  const labelSrc = nativeCta.querySelector('.order-button-text');
  const suffix = nativeCta.querySelector('.order-button-suffix');
  btn.appendChild(
    node('span', null, (labelSrc ? labelSrc.textContent : nativeCta.textContent || L.ctaFallback).trim())
  );
  if (suffix && suffix.textContent.trim()) {
    btn.appendChild(node('span', 'bs-co-bar__suffix', suffix.textContent.trim()));
  }
  const gate = step === 'shipping' ? createPaymentsGate() : null;
  btn.addEventListener('click', function () {
    if (gate && gate.intercept()) return;
    const live = findNativeCta() || nativeCta;
    live.click();
  });
  bar.appendChild(btn);

  document.body.appendChild(bar);
  bar._bsPriceObserver = mirrorPrice(amount);
  // teprve teď smí CSS skrýt nativní `.next-step` na mobilu
  document.documentElement.classList.add('bs-co-cta');
}

// Po vyprázdnění košíku musí lišta zmizet (nález B3).
function removeStickyCta() {
  const bar = document.querySelector('.bs-co-bar');
  if (bar) {
    if (bar._bsPriceObserver) bar._bsPriceObserver.disconnect();
    bar.remove();
  }
  document.documentElement.classList.remove('bs-co-cta');
}

// Nativní hlášky (`div.messages`) Shoptet renderuje až za obsahem → nad obsah
// levého sloupce; text i třídy zůstávají nativní. (Nález U5 #12.)
//
// ⚠️ Kotva NIKDY nesmí být `.cart-inner` — v kroku 1 je to naše split mřížka
// a hláška by v ní zabrala sloupec souhrnu (rekapitulace spadne pod obsah,
// celý layout se rozsype). Po AJAX přepočtu množství přitom hláška dorazí
// DŘÍV, než stojí levý sloupec, takže se umístění při každém průchodu
// přepočítává znovu — jednou usazená hláška se v dalším průchodu doputuje
// na správné místo. (Nález 2026-08-21.)
function messagesAnchor() {
  return (
    document.querySelector('#checkoutContent > .cart-content') ||
    document.querySelector('.cart-inner > .bs-co-main') ||
    document.querySelector('.bs-co-cart') ||
    document.querySelector('.cart-inner:not(.bs-co-grid)') ||
    document.querySelector('#content-wrapper .content-inner')
  );
}

function initMessages() {
  for (const box of document.querySelectorAll('.messages')) {
    if (!box.querySelector('.msg')) continue;
    const anchor = messagesAnchor();
    if (anchor && box.parentElement !== anchor) anchor.insertBefore(box, anchor.firstChild);
    if (box.dataset.bsCo) continue;
    box.dataset.bsCo = '1';
    box.classList.add('bs-co-msgs');
    if (box.querySelector('.msg-error')) box.scrollIntoView({ block: 'center' });
  }
}

// AJAX přepočet košíku vrací do `.cart-wrapper` čerstvý nativní `ol.cart-header`
// (ten náš už sedí v topbaru) → duplicitní seznam kroků se zahazuje. Když náš
// z topbaru zmizí, převezme roli ten nový. (Nález 2026-08-21.)
function syncStepper() {
  const holder = document.querySelector('.bs-co-head__steps');
  const ours = holder ? holder.querySelector('ol.cart-header') : null;
  if (!ours) {
    buildStepper();
    return;
  }
  for (const list of document.querySelectorAll('ol.cart-header')) {
    if (list !== ours) list.remove();
  }
}

// Mutace, kterou vyrobil náš vlastní tikající prvek (konec akce, odpočet) —
// pro sledování AJAX výměn košíku je to šum.
const OWN_LIVE_SEL = '.bs-sale-end, .bs-co-eta, .bs-buy__cd, .bs-co-xs';
function ownLiveMutation(mu) {
  const t = mu.target;
  const el = t && t.nodeType === 1 ? t : t && t.parentElement;
  return !!(el && el.closest && el.closest(OWN_LIVE_SEL));
}

// Čerstvý nativní markup košíku — AJAX vyměnil `.cart-inner` a naše vrstva
// ho ještě nepřestavěla (prázdný košík pozná `initEmptyCart` podle data-bs-co).
function freshCartMarkup() {
  const inner = document.querySelector('.cart-inner');
  if (!inner) return false;
  if (inner.classList.contains('cart-empty')) return !inner.dataset.bsCo;
  return !inner.classList.contains('bs-co-grid');
}

// Přepočet množství i odebrání položky vrací ČERSTVÉ uzly → dekorace znovu.
// Sledovat `#content-wrapper` (AJAX vymění celý `.cart-inner`). (Nález U5 #19.)
//
// Čerstvý markup se přestavuje HNED v callbacku, ne až po debounce: callback
// MutationObserveru běží jako mikroúloha po výměně DOM a před vykreslením
// snímku, takže se nativní košík neukáže ani na jeden snímek. Do 2026-09-22
// tu byl jen 80ms debounce a po odebrání položky problikl ~100 ms nenastylovaný
// košík (sonda ops/sweeps/2026-09-22-kostra-kategorie-kosik/probe-cart.mjs).
// Ostatní mutace (hláška, pozdní úpravy default JS) dál čekají na debounce.
function watchCartUpdates() {
  const root = document.getElementById('content-wrapper');
  if (!root || root.dataset.bsCoWatch) return;
  root.dataset.bsCoWatch = '1';

  let queued = false;
  const observer = new MutationObserver(function (muts) {
    // Vlastní živé prvky (odpočet konce akce, tik expedice) mutují DOM samy —
    // bez tohohle filtru se košík přestavoval každých 30 s (code review 2026-09-09, B1-02).
    if (muts.every(ownLiveMutation)) return;
    // hlášku přesunout HNED (platforma ji po ~3 s maže) — nález V2
    initMessages();
    if (freshCartMarkup()) {
      render();
      return;
    }
    // Odebrání selhalo: platforma vloží hlášku a obsah nevymění → košík zpět.
    if (cartSkeletonActive() && document.querySelector('.messages .msg-error')) {
      quiet(endCartSkeleton);
    }
    refresh();
  });
  const watch = function () {
    observer.observe(root, { childList: true, subtree: true });
  };
  // vlastní zásah do DOM bez vlastní reakce (mutace mimo observer nejdou)
  const quiet = function (fn) {
    observer.disconnect();
    try {
      fn();
    } finally {
      watch();
    }
  };
  const render = function () {
    observer.disconnect();
    const fresh = freshCartMarkup();
    const emptied = !!document.querySelector('.cart-inner.cart-empty');
    if (emptied) {
      initEmptyCart();
      removeStickyCta();
      document.documentElement.classList.remove('bs-co-step-cart');
      document.documentElement.classList.add('bs-co-step-empty');
    } else {
      initCart();
      initSummary('cart');
    }
    syncStepper();
    initMessages();
    refreshTrust();
    // kostra z odebrání položky končí až s přestavěným NOVÝM košíkem
    if (fresh) endCartSkeleton();
    watch();
  };
  const refresh = function () {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      // Během kostry nesahat na starý košík — přestavba by do ní vrátila čitelný
      // text; nový markup obslouží callback výš, chybu hláška nebo timeout.
      if (cartSkeletonActive() && !freshCartMarkup()) return;
      render();
    }, 80);
  };
  // Odebrání položky: od kliknutí na × do odpovědi kostra (cartSkeleton.js).
  // Capture na window = dřív než delegovaný handler default JS, který mazání
  // posílá AJAXem; klik samotný nerušíme.
  window.addEventListener(
    'click',
    function (e) {
      const btn = e.target && e.target.closest ? e.target.closest('table.cart-table button.remove-item') : null;
      if (!btn) return;
      const row = btn.closest('tr.removeable');
      quiet(function () {
        startCartSkeleton(row);
      });
    },
    true
  );
  onCartUpdated(refresh);
  watch();
}

// Kroky 2–3: AJAX přepisuje obsah `#summary-box` (změna metody) a odnáší naše
// thumby i progress → souhrn se po každé mutaci dekoruje znovu (idempotentní).
function watchSummaryUpdates(step) {
  const root = document.getElementById('checkoutSidebar');
  if (!root || root.dataset.bsCoWatch) return;
  root.dataset.bsCoWatch = '1';

  let queued = false;
  const observer = new MutationObserver(function () {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      observer.disconnect();
      initSummary(step);
      initMessages();
      watch();
    }, 80);
  });
  const watch = function () {
    observer.observe(root, { childList: true, subtree: true });
  };
  watch();
}

// --- init ------------------------------------------------------------------
function detectStep() {
  const b = document.body.classList;
  if (!b.contains('ordering-process')) return null;
  if (document.querySelector('.cart-inner.cart-empty')) return 'empty';
  if (b.contains('in-krok-1')) return 'shipping';
  if (b.contains('in-krok-2')) return 'info';
  // POZOR: děkovací stránka (`in-dekujeme`) má `table.cart-table` taky (nese ji
  // rekapitulace) — bez téhle větve by spadla do `cart`.
  if (b.contains(bodyClass('checkoutDone'))) return 'done';
  if (b.contains(bodyClass('cart')) || document.querySelector('table.cart-table')) return 'cart';
  return 'other';
}

export function initCheckoutPage() {
  const step = detectStep();
  if (!step) return;

  // loga dopravců a shopu jsou v pokladně vidět hned, takže se sprite nečeká
  // na `load` jako jinde (#313); `ensureLogos()` je idempotentní
  ensureLogos();

  buildHead();
  buildFoot();
  buildStepper();

  if (step === 'cart') {
    initCart();
    watchCartUpdates();
  } else if (step === 'empty') initEmptyCart();
  else if (step === 'shipping') initShipping();
  else if (step === 'info') initInfo();
  else if (step === 'done') initDone();

  if (step !== 'empty' && step !== 'done') {
    initSummary(step);
    if (step !== 'cart') watchSummaryUpdates(step);
    initStickyCta(step);
  }
  initMessages();

  document.documentElement.classList.add('bs-co-ready');
  document.documentElement.classList.add('bs-co-step-' + step);
}
